import React from 'react';
import classNames from 'classnames';

import css from './IconCard.module.css';

const BRAND_AMEX = 'amex';
const BRAND_DINERS = 'diners';
const BRAND_DISCOVERY = 'discovery';
const BRAND_JCB = 'jcb';
const BRAND_MASTERCARD = 'mastercard';
const BRAND_UNIONPAY = 'unionpay';
const BRAND_VISA = 'visa';
const CLOCK = 'clock';
const USERS = 'users';
const RATING_STAR = 'ratingStar';
const EMPTY_HEART = 'emptyHeart';
const ACCORDION_PLUS = 'accordionPlus';
const ACCORDION_MINUS = 'accordionMinus';
const creditCard = 'creditCard';

// Training Program Icons
const car = 'car';
const barman = 'barman';
const sewing = 'sewing';
const bien_etre_corporel = 'bien_etre_corporel';
const aesthetics = 'aesthetics';
const hairstyle = 'hairstyle';
const automobile = 'automobile';
const construction = 'construction';
const photoVideo = 'photo-video';
const business = 'business';
const realEstate = 'real-estate';
const crafts = 'crafts';
const jewelryWatches = 'jewelry-watches';
const well_being = 'well-being';
const hairdressing = 'hairdressing';
const horecaorbarista = 'horeca-or-barista';
const others = 'others';

/**
 * Normalize category/brand keys so accented and aliased ids match the same icon.
 * e.g. "Bien-être" / "bien-etre" / "bien-être" (NFD) -> "bien-etre"
 */
const normalizeBrandKey = value => {
  if (typeof value !== 'string') {
    return value;
  }
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
};

const resolveBrand = brand => {
  const key = normalizeBrandKey(brand);
  const aliases = {
    'well-being': well_being,
    'bien-etre': well_being,
    bien_etre_corporel: well_being,
    hairdressing: hairdressing,
    coiffure: hairdressing,
    hairstyle: hairdressing,
    'horeca-or-barista': horecaorbarista,
    barman: horecaorbarista,
  };
  return aliases[key] || brand;
};

// new Icons
const LOCATION_LINE = 'locationLine';
const CLOKC_LINE = 'clockLine';
const filter_icon = 'filterIcon';

// Comment commencer card icons

const teachingOnBoard = 'teachingOnBoard';
const chooseFingerPoint = 'chooseFingerPoint';
const tvRecordingCamera = 'tvRecordingCamera';
const ideaBulb = 'ideaBulb';
const createUserPlus = 'createUserPlus';
const flyingPaperPlan = 'flyingPaperPlan';
const mail = 'mail';
const circularCheck = 'circularCheck';
const scalapay = 'scalapay';
const exclamationCircle = 'exclamationCircle';
const Flexible = 'Flexible';
const LearningToday = 'LearningToday';
const SecureCheckout = 'SecureCheckout';

// listing icons

const circularRightArrow = 'circularRightArrow';

/**
 * Payment card icon.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {'amex' | 'diners' | 'discovery' | 'jcb' | 'mastercard' | 'unionpay' | 'visa'} props.brand payment card provider
 * @returns {JSX.Element} SVG icon
 */
const IconCard = props => {
  const { className, rootClassName, brand: brandProp = 'default' } = props;
  const brand = resolveBrand(brandProp);
  const classes = classNames(rootClassName || css.root, className);
  const roleInfo = {
    role: 'img',
    ['aria-label']: brandProp,
  };

  switch (brand) {
    case BRAND_AMEX:
      return (
        <svg
          className={classes}
          height="16"
          viewBox="0 0 24 16"
          width="24"
          xmlns="http://www.w3.org/2000/svg"
          {...roleInfo}
        >
          <g fill="none" fillRule="evenodd">
            <rect fill="#016fd0" height="16" rx="2" width="24" />
            <path
              d="M13.764 13.3939V7.6925l10.1476.009v1.575l-1.1729 1.2534 1.1729 1.2648v1.6083H22.039l-.9952-1.0981-.9881 1.1023z"
              fill="#fffffe"
            />
            <path
              d="M14.4418 12.7688V8.3199h3.7723v1.0249h-2.5509v.6957h2.49v1.0078h-2.49v.6833h2.5509v1.0372z"
              fill="#016fd0"
            />
            <path
              d="M18.1953 12.7688l2.0873-2.227L18.1952 8.32h1.6156l1.2755 1.41 1.2791-1.41h1.5462v.035l-2.0429 2.1868 2.0429 2.1639v.0631h-1.5617l-1.2981-1.4242-1.2848 1.4242z"
              fill="#016fd0"
            />
            <path
              d="M14.2373 2.632h2.4461l.8591 1.9508V2.632h3.0199l.5207 1.4615.5226-1.4615h2.3059v5.7013H11.7251z"
              fill="#fffffe"
            />
            <g fill="#016fd0">
              <path d="M14.7005 3.2514l-1.974 4.4451h1.3539l.3724-.8901h2.018l.372.8901h1.3877l-1.9658-4.4451zm.1696 2.5574l.592-1.415.5916 1.415z" />
              <path d="M18.212 7.6957v-4.445l1.903.0065.9794 2.7327.9856-2.7392h1.8316v4.445l-1.1786.0105V4.6528l-1.1126 3.043h-1.0758L19.409 4.6423v3.0533z" />
            </g>
          </g>
        </svg>
      );

    case creditCard:
      return (
        <svg
          className={classes}
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          {...roleInfo}
        >
          <path
            d="M17.4997 3.33336H2.49967C1.5792 3.33336 0.833008 4.07955 0.833008 5.00003V15C0.833008 15.9205 1.5792 16.6667 2.49967 16.6667H17.4997C18.4202 16.6667 19.1663 15.9205 19.1663 15V5.00003C19.1663 4.07955 18.4202 3.33336 17.4997 3.33336Z"
            stroke="#4DAF4E"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M0.833008 8.33336H19.1663"
            stroke="#4DAF4E"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <line
            x1="4.24023"
            y1="12.038"
            x2="6.49023"
            y2="12.038"
            stroke="#4DAF4E"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      );
    case Flexible:
      return (
        <svg
          width="52"
          height="52"
          viewBox="0 0 52 52"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M25.9997 47.6667C37.9658 47.6667 47.6663 37.9662 47.6663 26C47.6663 14.0339 37.9658 4.33337 25.9997 4.33337C14.0335 4.33337 4.33301 14.0339 4.33301 26C4.33301 37.9662 14.0335 47.6667 25.9997 47.6667Z"
            stroke="#0171FE"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
          <path
            d="M35.3332 16.6666L16.6665 35.3333"
            stroke="#0171FE"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
          <path
            d="M18.6668 22C20.5078 22 22.0002 20.5077 22.0002 18.6667C22.0002 16.8258 20.5078 15.3334 18.6668 15.3334C16.8259 15.3334 15.3335 16.8258 15.3335 18.6667C15.3335 20.5077 16.8259 22 18.6668 22Z"
            stroke="#0171FE"
            strokeWidth="1.5"
            strokeLinecap="round"
            fill="none"
            strokeLinejoin="round"
          />
          <path
            d="M33.3333 36.6667C35.1743 36.6667 36.6667 35.1743 36.6667 33.3333C36.6667 31.4924 35.1743 30 33.3333 30C31.4924 30 30 31.4924 30 33.3333C30 35.1743 31.4924 36.6667 33.3333 36.6667Z"
            stroke="#0171FE"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      );
    case LearningToday:
      return (
        <svg
          width="52"
          height="52"
          viewBox="0 0 52 52"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M25.9997 47.6667C37.9658 47.6667 47.6663 37.9662 47.6663 26C47.6663 14.0339 37.9658 4.33337 25.9997 4.33337C14.0335 4.33337 4.33301 14.0339 4.33301 26C4.33301 37.9662 14.0335 47.6667 25.9997 47.6667Z"
            stroke="#0171FE"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
          <path
            d="M26.0002 39.3333C26.0002 39.3333 36.6668 34 36.6668 26V16.6666L26.0002 12.6666L15.3335 16.6666V26C15.3335 34 26.0002 39.3333 26.0002 39.3333Z"
            stroke="#0171FE"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
          <path
            d="M30.4455 21.3334L24.3344 27.4445L21.5566 24.6667"
            stroke="#0171FE"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      );
    case SecureCheckout:
      return (
        <svg
          width="52"
          height="52"
          viewBox="0 0 52 52"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M25.9997 47.6667C37.9658 47.6667 47.6663 37.9662 47.6663 26C47.6663 14.0339 37.9658 4.33337 25.9997 4.33337C14.0335 4.33337 4.33301 14.0339 4.33301 26C4.33301 37.9662 14.0335 47.6667 25.9997 47.6667Z"
            stroke="#0171FE"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
          <path
            d="M33.7695 13.9943C33.9177 13.935 34.0833 13.935 34.2314 13.9943L39.9463 16.2804C40.0614 16.3266 40.16 16.4062 40.2295 16.5089C40.299 16.6117 40.3359 16.7334 40.3359 16.8575V22.7609C40.3322 23.6929 40.1246 24.6135 39.7266 25.4562C39.332 26.2915 38.7595 27.03 38.0508 27.6222V34.7775C38.0504 35.3422 37.8261 35.884 37.4268 36.2833C37.0274 36.6826 36.4856 36.907 35.9209 36.9073H13.7949C13.2302 36.907 12.6884 36.6826 12.2891 36.2833C11.8898 35.884 11.6654 35.3422 11.665 34.7775V19.5089C11.6653 18.9442 11.8898 18.4024 12.2891 18.0031C12.6884 17.6037 13.2301 17.3793 13.7949 17.379H26.001C26.1656 17.3791 26.324 17.4442 26.4404 17.5607C26.5568 17.6772 26.6221 17.8354 26.6221 18.0001C26.622 18.1648 26.5569 18.3231 26.4404 18.4396C26.324 18.556 26.1656 18.6211 26.001 18.6212H13.7949C13.5597 18.6215 13.3343 18.7156 13.168 18.882C13.0017 19.0483 12.9075 19.2738 12.9072 19.5089V20.8068H26.001C26.1656 20.8069 26.324 20.8729 26.4404 20.9894C26.5568 21.1059 26.6221 21.2642 26.6221 21.4288C26.622 21.5935 26.5569 21.7518 26.4404 21.8683C26.324 21.9847 26.1656 22.0498 26.001 22.0499H12.9072V34.7804C12.9075 35.0156 13.0017 35.241 13.168 35.4073C13.3343 35.5737 13.5597 35.6678 13.7949 35.6681H35.9209C36.156 35.6677 36.3816 35.5736 36.5479 35.4073C36.714 35.2411 36.8073 35.0155 36.8076 34.7804V28.4679L34.3203 29.962C34.2239 30.0198 34.1133 30.0499 34.001 30.0499C33.8885 30.0499 33.7772 30.0199 33.6807 29.962L30.7412 28.1984C29.8049 27.633 29.0301 26.8357 28.4912 25.8839C27.9524 24.9321 27.6681 23.8575 27.665 22.7638V16.8575C27.6651 16.7334 27.7019 16.6117 27.7715 16.5089C27.841 16.4062 27.9396 16.3266 28.0547 16.2804L33.7695 13.9943ZM28.9072 17.2775V22.7628C28.9095 23.6421 29.139 24.5064 29.5723 25.2716C30.0056 26.0368 30.6289 26.6777 31.3818 27.132L34 28.7042L36.6191 27.132C37.3721 26.6777 37.9954 26.0368 38.4287 25.2716C38.8619 24.5066 39.0913 23.643 39.0938 22.7638V17.2775L34 15.2394L28.9072 17.2775Z"
            fill="#0171FE"
            stroke="#0171FE"
            strokeWidth="0.1"
          />
          <path
            d="M36.2856 19.3801C36.4504 19.38 36.6084 19.4454 36.7251 19.5618C36.8418 19.6783 36.9076 19.8363 36.9077 20.0012C36.9078 20.1661 36.8425 20.324 36.7261 20.4407L33.6782 23.4895C33.5617 23.6059 33.4035 23.6711 33.2388 23.6711C33.0741 23.6711 32.9158 23.6059 32.7993 23.4895L31.2749 21.9661C31.2172 21.9083 31.1713 21.8393 31.1401 21.7639C31.1089 21.6885 31.0932 21.6073 31.0933 21.5256C31.0933 21.4442 31.109 21.3635 31.1401 21.2883C31.1714 21.2129 31.2181 21.1439 31.2759 21.0862C31.3336 21.0286 31.4018 20.9826 31.4771 20.9514C31.5525 20.9202 31.6337 20.9045 31.7153 20.9045C31.797 20.9046 31.8782 20.9211 31.9536 20.9524C32.0288 20.9837 32.0972 21.0295 32.1548 21.0872L33.2378 22.1711L35.8462 19.5627C35.9627 19.4461 36.1208 19.3803 36.2856 19.3801Z"
            fill="#0171FE"
            stroke="#0171FE"
            strokeWidth="0.1"
          />
          <path
            d="M20.2856 32.2358C20.4504 32.2358 20.6086 32.3011 20.7251 32.4175C20.8416 32.534 20.9077 32.6922 20.9077 32.8569C20.9077 33.0217 20.8416 33.1798 20.7251 33.2964C20.6086 33.4129 20.4504 33.478 20.2856 33.478H15.7144C15.5496 33.478 15.3914 33.4129 15.2749 33.2964C15.1584 33.1799 15.0933 33.0217 15.0933 32.8569C15.0933 32.6922 15.1584 32.534 15.2749 32.4175C15.3914 32.301 15.5496 32.2359 15.7144 32.2358H20.2856Z"
            fill="#0171FE"
            stroke="#0171FE"
            strokeWidth="0.1"
          />
        </svg>
      );
    case filter_icon:
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={24}
          height={24}
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
        >
          <path
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75"
          />
        </svg>
      );
    case ACCORDION_PLUS:
      return (
        <svg
          width="32"
          height="32"
          viewBox="0 0 32 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect width="32" height="32" rx="16" fill="#EEF5FF" />
          <path
            d="M16 9V23"
            stroke="#0171FE"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M9 16H23"
            stroke="#0171FE"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );

    case ACCORDION_MINUS:
      return (
        <svg
          width="32"
          height="32"
          viewBox="0 0 32 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clipPath="url(#clip0_613_235)">
            <path
              d="M32 16C32 7.16344 24.8366 0 16 0C7.16344 0 0 7.16344 0 16C0 24.8366 7.16344 32 16 32C24.8366 32 32 24.8366 32 16Z"
              fill="#EEF5FF"
            />
            <path
              d="M9 16H23"
              stroke="#0171FE"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
          <defs>
            <clipPath id="clip0_613_235">
              <rect width="32" height="32" fill="white" />
            </clipPath>
          </defs>
        </svg>
      );
    case exclamationCircle:
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <g clipPath="url(#clip0_1467_547)">
            <path
              d="M7.99967 14.6667C11.6816 14.6667 14.6663 11.6819 14.6663 8.00004C14.6663 4.31814 11.6816 1.33337 7.99967 1.33337C4.31778 1.33337 1.33301 4.31814 1.33301 8.00004C1.33301 11.6819 4.31778 14.6667 7.99967 14.6667Z"
              stroke="#0171FE"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M8 5.33337V8.00004"
              stroke="#0171FE"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M8 10.6666H8.00667"
              stroke="#0171FE"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
          <defs>
            <clipPath id="clip0_1467_547">
              <rect width="16" height="16" fill="white" />
            </clipPath>
          </defs>
        </svg>
      );
    case scalapay:
      return (
        <svg
          className={classes}
          xmlns="http://www.w3.org/2000/svg"
          width="212"
          height="40"
          viewBox="0 0 212 40"
          fill="none"
          {...roleInfo}
        >
          <path
            d="M15.4665 33.0946C15.0345 33.0858 14.6209 32.9185 14.3042 32.6245C14.1844 32.5103 14.073 32.3875 13.9709 32.2571L0.673055 17.1046C0.561954 16.9765 0.450854 16.8568 0.348299 16.7201C0.122336 16.4227 0 16.0595 0 15.686C0 15.3125 0.122336 14.9493 0.348299 14.6519C0.455447 14.5232 0.569568 14.4006 0.690147 14.2844L6.61266 8.19097C6.73231 8.07132 6.84341 7.95167 6.9716 7.84057C7.28179 7.57348 7.67754 7.42657 8.08688 7.42657C8.49622 7.42657 8.89197 7.57348 9.20216 7.84057C9.36454 7.98586 9.50982 8.14823 9.66365 8.30207L13.8171 12.5752L14.2701 13.0367C14.4812 13.2341 14.7406 13.3727 15.0221 13.4383C15.2778 13.5053 15.546 13.5092 15.8035 13.4497C16.061 13.3901 16.3003 13.2689 16.5006 13.0965C16.663 12.9597 16.8083 12.7974 16.9621 12.6435L21.013 8.46444C21.1668 8.31061 21.3121 8.14823 21.4745 8.00295C21.788 7.72187 22.1943 7.56643 22.6154 7.56643C23.0365 7.56643 23.4428 7.72187 23.7563 8.00295C23.8845 8.11405 23.9956 8.2337 24.1153 8.35334L29.9694 14.3955L30.2173 14.6519C30.4987 14.9523 30.6624 15.3441 30.6781 15.7555C30.6938 16.1668 30.5605 16.57 30.3027 16.891L30.0207 17.2157C25.6906 22.1782 21.3548 27.1407 17.0134 32.1032C16.8766 32.2656 16.7399 32.4365 16.5861 32.5904C16.2851 32.8867 15.8879 33.0656 15.4665 33.0946Z"
            fill="black"
          />
          <path
            d="M141.403 24.7108V10.396C141.403 10.1567 141.403 9.91737 141.403 9.67807C141.417 9.63347 141.443 9.59338 141.477 9.56186C141.512 9.53034 141.554 9.50851 141.6 9.4986H141.959H146.129C146.352 9.4986 146.446 9.59261 146.454 9.79772C146.463 10.0028 146.454 10.225 146.454 10.4472C146.454 10.6694 146.454 10.7293 146.454 10.8745C146.454 11.0198 146.582 10.9771 146.625 10.9429L147.163 10.5754C148.461 9.69613 149.974 9.18786 151.539 9.10548C153.126 8.98475 154.718 9.28084 156.155 9.9644C157.593 10.648 158.827 11.6953 159.735 13.0025C160.677 14.265 161.318 15.7269 161.607 17.2756C161.798 18.1654 161.884 19.0748 161.863 19.9848C161.843 21.8899 161.343 23.7592 160.41 25.4202C159.446 27.0887 158.012 28.4368 156.287 29.2968C154.563 30.1569 152.623 30.4909 150.71 30.2573C149.566 30.1084 148.47 29.7023 147.505 29.0694L146.899 28.6763C146.878 28.6662 146.855 28.6615 146.832 28.6626C146.809 28.6637 146.787 28.6705 146.768 28.6824C146.748 28.6943 146.732 28.711 146.72 28.7308C146.709 28.7506 146.703 28.773 146.702 28.7959C146.702 29.0352 146.702 29.2745 146.702 29.5138V38.9744C146.702 39.1625 146.702 39.359 146.702 39.547C146.702 39.9487 146.651 40 146.249 40H141.788C141.514 40 141.438 39.9231 141.429 39.6667C141.42 39.4103 141.429 39.3846 141.429 39.2394V24.7108H141.403ZM146.693 19.7028C146.693 20.6087 146.693 21.5231 146.693 22.429C146.69 22.6477 146.766 22.8601 146.907 23.0272C147.406 23.6701 148.035 24.2002 148.753 24.5826C150.03 25.2844 151.533 25.4504 152.932 25.0441C153.668 24.8296 154.343 24.4469 154.905 23.9262C155.468 23.4056 155.901 22.7614 156.171 22.0444C156.543 21.1069 156.684 20.0941 156.585 19.0906C156.485 18.0871 156.147 17.122 155.599 16.2757C155.26 15.7503 154.82 15.2971 154.305 14.9421C153.79 14.5872 153.21 14.3375 152.599 14.2076C151.916 14.0708 151.213 14.0708 150.531 14.2076C149.101 14.4556 147.81 15.215 146.899 16.3441C146.741 16.5232 146.655 16.7548 146.659 16.9936C146.693 17.8824 146.693 18.7883 146.693 19.7028Z"
            fill="black"
          />
          <path
            d="M135.319 19.7114V29.0609C135.319 29.9155 135.404 29.9155 134.515 29.9155H130.781C130.285 29.9155 130.251 29.9155 130.234 29.3857C130.234 29.1036 130.234 28.8131 130.234 28.531C130.234 28.531 130.165 28.4627 130.123 28.4627C130.076 28.4685 130.032 28.4862 129.994 28.514L129.704 28.7276C128.252 29.8142 126.475 30.3775 124.662 30.3257C123.025 30.3061 121.419 29.8737 119.994 29.0684C118.569 28.2632 117.37 27.1113 116.509 25.7193C115.633 24.2815 115.087 22.6674 114.91 20.9933C114.686 19.0798 114.945 17.1407 115.662 15.3528C116.318 13.6398 117.429 12.1385 118.876 11.0113C120.11 10.0255 121.585 9.38815 123.149 9.16536C125.428 8.8147 127.754 9.35652 129.644 10.678L130.063 10.943C130.085 10.9437 130.108 10.9395 130.128 10.9306C130.149 10.9218 130.167 10.9085 130.182 10.8917C130.196 10.85 130.196 10.8052 130.182 10.7635C130.182 10.5755 130.182 10.3789 130.182 10.1909C130.182 9.50721 130.182 9.49012 130.849 9.49012H134.584C134.753 9.48736 134.922 9.50167 135.088 9.53285C135.151 9.5557 135.203 9.6015 135.233 9.66104C135.251 9.7312 135.251 9.80454 135.233 9.8747C135.233 10.1396 135.233 10.4046 135.233 10.6695L135.319 19.7114ZM130.029 19.7114C130.029 18.7969 130.029 17.891 130.029 16.9766C130.04 16.7381 129.957 16.5047 129.798 16.3271C129.035 15.3925 128.012 14.7054 126.858 14.3529C125.986 14.0831 125.062 14.0332 124.166 14.2076C123.547 14.3307 122.96 14.5772 122.438 14.9326C121.917 15.288 121.473 15.7448 121.132 16.2758C120.645 16.99 120.327 17.8052 120.201 18.6602C119.968 19.9186 120.144 21.2187 120.705 22.3692C120.99 22.9799 121.396 23.5265 121.898 23.9759C122.401 24.4254 122.989 24.7684 123.628 24.9844C124.241 25.1893 124.888 25.2762 125.533 25.2408C126.374 25.2185 127.198 25.0081 127.947 24.6251C128.695 24.242 129.348 23.696 129.858 23.0273C130 22.8614 130.073 22.6474 130.063 22.4291C130.02 21.5232 130.029 20.6087 130.029 19.7028V19.7114Z"
            fill="black"
          />
          <path
            d="M185.689 19.6685V28.9412C185.689 29.1548 185.689 29.3685 185.689 29.5907C185.689 29.8129 185.595 29.8727 185.373 29.8898H180.955C180.775 29.8898 180.681 29.7958 180.681 29.6249C180.681 29.3172 180.681 29.001 180.681 28.6933C180.684 28.6224 180.67 28.5519 180.638 28.4882C180.638 28.4882 180.544 28.4284 180.519 28.4455C180.394 28.5143 180.274 28.5914 180.16 28.6762C179.644 29.0644 179.085 29.3913 178.493 29.6505C177.567 30.0523 176.572 30.2728 175.562 30.3C174.18 30.3627 172.802 30.1264 171.52 29.6072C170.238 29.088 169.084 28.2982 168.135 27.2917C167.024 26.1226 166.21 24.7042 165.759 23.1554C165.295 21.6641 165.138 20.0941 165.298 18.5404C165.45 16.7919 166.009 15.1031 166.93 13.6093C167.747 12.2737 168.876 11.1563 170.221 10.3532C171.536 9.55186 173.039 9.10982 174.579 9.07126C176.066 8.99286 177.55 9.2866 178.895 9.92588C179.305 10.1216 179.697 10.3532 180.066 10.6181L180.485 10.8916C180.536 10.8916 180.655 10.8916 180.655 10.8147C180.664 10.7209 180.664 10.6265 180.655 10.5327C180.655 10.2677 180.655 10.0028 180.655 9.73786C180.652 9.7021 180.656 9.66598 180.667 9.63185C180.678 9.59773 180.697 9.56635 180.721 9.53975C180.745 9.51316 180.775 9.49195 180.807 9.47749C180.84 9.46303 180.876 9.45565 180.912 9.45583H185.185C185.326 9.46025 185.466 9.47741 185.604 9.50711C185.604 9.50711 185.689 9.55839 185.706 9.60112C185.719 9.66888 185.719 9.73847 185.706 9.80623C185.706 10.0199 185.706 10.2421 185.706 10.4557L185.689 19.6685ZM180.399 19.6685V16.9423C180.403 16.8231 180.383 16.7043 180.34 16.5928C180.297 16.4814 180.233 16.3794 180.151 16.2928C179.253 15.184 177.984 14.4373 176.579 14.1904C175.705 14.0157 174.801 14.0716 173.955 14.3528C173.351 14.5548 172.792 14.8749 172.313 15.2945C171.833 15.7141 171.441 16.2248 171.161 16.797C170.741 17.5787 170.497 18.4428 170.447 19.3287C170.397 20.2145 170.541 21.1006 170.87 21.9247C171.229 22.9374 171.906 23.807 172.799 24.4044C173.692 25.0019 174.754 25.2951 175.827 25.2407C176.668 25.2252 177.496 25.0206 178.247 24.6418C178.999 24.2631 179.655 23.72 180.168 23.0528C180.323 22.8695 180.402 22.6343 180.391 22.3948L180.399 19.6685Z"
            fill="black"
          />
          <path
            d="M98.9116 29.7873C98.8697 29.819 98.8237 29.8449 98.7749 29.8642C98.6782 29.8725 98.581 29.8725 98.4843 29.8642H94.3138C94.2202 29.8766 94.1253 29.8766 94.0317 29.8642C93.9924 29.842 93.9599 29.8095 93.9377 29.7702C93.9249 29.6996 93.9249 29.6272 93.9377 29.5566C93.9377 29.2489 93.9377 28.9412 93.9377 28.625C93.9377 28.5567 93.9377 28.4627 93.8352 28.4627C93.7685 28.4583 93.7023 28.4763 93.6471 28.5139C93.5667 28.566 93.4897 28.6231 93.4164 28.6849C91.9418 29.7948 90.1336 30.3704 88.2887 30.3172C86.6431 30.2989 85.0292 29.8627 83.5986 29.0494C82.1679 28.2362 80.9675 27.0726 80.11 25.668C79.1783 24.1521 78.6244 22.4346 78.4947 20.66C78.3461 18.8842 78.6063 17.0979 79.2554 15.4382C79.8669 13.8124 80.8815 12.3685 82.2038 11.2421C83.4936 10.112 85.086 9.38417 86.7846 9.14825C89.0476 8.78439 91.3639 9.30456 93.254 10.6011C93.4101 10.7107 93.5728 10.8106 93.7412 10.9002C93.7412 10.9002 93.8523 10.9002 93.8523 10.8404C93.8523 10.6011 93.8523 10.3618 93.8523 10.1225C93.8523 9.43882 93.8523 9.44737 94.5189 9.44737H98.621C98.8176 9.44737 98.9031 9.54992 98.9031 9.77212C98.9031 9.99432 98.9031 10.1054 98.9031 10.2763V29.078C98.9413 29.3127 98.9441 29.5518 98.9116 29.7873ZM93.6642 19.6601C93.6642 18.8055 93.6642 17.9508 93.6642 17.0962C93.6686 16.9301 93.639 16.7648 93.5773 16.6105C93.5155 16.4563 93.423 16.3162 93.3053 16.1989C92.7204 15.5143 91.9865 14.9727 91.1599 14.6157C90.3333 14.2586 89.4359 14.0957 88.5365 14.1392C87.4714 14.1658 86.4492 14.565 85.6479 15.2673C85.0403 15.7774 84.5628 16.4249 84.2549 17.156C83.6001 18.5899 83.5206 20.2204 84.0327 21.7112C84.261 22.4724 84.6679 23.1682 85.2193 23.7405C85.7708 24.3128 86.451 24.7451 87.2033 25.0014C87.8138 25.2196 88.4619 25.3126 89.1091 25.2749C89.9296 25.2604 90.737 25.0671 91.475 24.7084C92.213 24.3497 92.8639 23.8343 93.3822 23.1982C93.59 22.963 93.7001 22.6573 93.6899 22.3436C93.6472 21.4291 93.6642 20.5403 93.6642 19.6601Z"
            fill="black"
          />
          <path
            d="M194.963 9.50721C195.099 9.78923 195.219 10.0285 195.33 10.2678L200.355 20.9335C200.426 21.1616 200.545 21.3716 200.706 21.5488C200.859 21.4804 200.894 21.3351 200.945 21.2069L201.945 18.8995C203.21 15.9995 204.469 13.0995 205.722 10.1994C205.816 9.97725 205.927 9.76359 206.038 9.53285C206.177 9.50455 206.316 9.48457 206.457 9.47302H210.918C211.084 9.47877 211.25 9.49303 211.414 9.51575C211.426 9.51639 211.438 9.51981 211.448 9.52574C211.458 9.53167 211.467 9.53994 211.474 9.54989C211.48 9.55985 211.485 9.57122 211.486 9.5831C211.488 9.59497 211.486 9.60703 211.482 9.61831C211.453 9.75878 211.41 9.89612 211.354 10.0285C210.784 11.2763 210.232 12.524 209.696 13.7718L198.364 39.2565C198.276 39.512 198.152 39.7537 197.996 39.9744H197.655H192.98C192.883 39.9874 192.786 39.9874 192.689 39.9744C192.671 39.962 192.656 39.9455 192.646 39.9262C192.635 39.9068 192.63 39.8853 192.629 39.8633C192.656 39.7471 192.69 39.6329 192.732 39.5214L193.706 37.3593L197.603 28.6336C197.697 28.437 197.783 28.2405 197.868 28.0439C197.9 27.9787 197.916 27.9071 197.916 27.8345C197.916 27.762 197.9 27.6904 197.868 27.6251L197.561 26.9756L189.502 10.2507C189.407 10.0542 189.331 9.8576 189.245 9.66104C189.245 9.60976 189.305 9.49866 189.356 9.49011H189.638H194.458C194.629 9.47302 194.766 9.49011 194.963 9.50721Z"
            fill="black"
          />
          <path
            d="M71.9915 22.6341L72.3334 22.8734C73.5127 23.8135 74.7434 24.6852 75.88 25.6766C75.88 25.6766 75.88 25.7193 75.88 25.7364C75.8897 25.7815 75.8897 25.8281 75.88 25.8731C74.8155 27.4011 73.3646 28.6191 71.6753 29.4027C70.5946 29.8853 69.4375 30.1746 68.2568 30.2573C67.6107 30.2958 66.9629 30.2958 66.3168 30.2573C64.1683 30.1454 62.1094 29.3606 60.4315 28.014C58.7536 26.6673 57.5417 24.8271 56.9673 22.7538C56.6084 21.5318 56.4723 20.2551 56.5656 18.9849C56.6484 17.2314 57.1748 15.5275 58.0956 14.0328C59.0163 12.5381 60.3012 11.3015 61.8301 10.4387C63.3303 9.56972 65.0278 9.099 66.7612 9.07133C68.6885 8.99547 70.5991 9.45391 72.2821 10.396C73.3975 11.011 74.384 11.8351 75.1878 12.8231C75.2469 12.8976 75.2984 12.9777 75.3416 13.0624C75.3522 13.0836 75.3577 13.107 75.3577 13.1308C75.3577 13.1545 75.3522 13.1779 75.3416 13.1991C75.2656 13.2907 75.1828 13.3764 75.0938 13.4555L72.1197 16.1732L71.8462 16.404C71.8295 16.421 71.8096 16.4346 71.7876 16.4438C71.7656 16.4531 71.7419 16.4578 71.718 16.4578C71.6942 16.4578 71.6705 16.4531 71.6485 16.4438C71.6265 16.4346 71.6066 16.421 71.5898 16.404L71.2565 16.0279C70.8508 15.5743 70.3781 15.1852 69.855 14.8742C69.1548 14.4391 68.3623 14.1745 67.5412 14.1017C66.7201 14.0289 65.8933 14.15 65.1276 14.4551C64.3618 14.7603 63.6784 15.241 63.1323 15.8585C62.5863 16.4761 62.193 17.2133 61.9839 18.0106C61.643 19.2272 61.6847 20.5192 62.1036 21.7112C62.428 22.6982 63.0447 23.5635 63.8718 24.1923C64.699 24.821 65.6977 25.1837 66.7356 25.2322C67.6354 25.3006 68.5382 25.1488 69.3662 24.79C70.1941 24.4311 70.9221 23.876 71.4873 23.1725C71.6326 23.0102 71.7608 22.8392 71.9061 22.6769C71.9232 22.6598 71.9488 22.6598 71.9915 22.6341Z"
            fill="black"
          />
          <path
            d="M52.6173 11.1481C51.7627 12.4385 51.0448 13.7119 50.1817 14.9426C50.1817 14.9426 50.1817 14.9426 50.0877 14.9853L49.5663 14.6605C48.8756 14.2196 48.1069 13.915 47.3016 13.7632C46.5703 13.6163 45.8145 13.6456 45.0967 13.8487C44.8195 13.9217 44.5586 14.0464 44.3275 14.2161C44.1729 14.3309 44.0373 14.4693 43.9259 14.6264C43.7584 14.8486 43.6804 15.1255 43.7073 15.4024C43.7342 15.6794 43.864 15.9361 44.0711 16.1219C44.2611 16.3078 44.4806 16.4609 44.7206 16.5749C45.071 16.7287 45.4214 16.8911 45.7804 17.0278C46.4299 17.2671 47.0879 17.4893 47.7375 17.7201C48.44 17.9574 49.1253 18.2429 49.7885 18.5747C50.3022 18.8355 50.7864 19.1507 51.2328 19.5148C51.8239 19.9894 52.3067 20.585 52.6486 21.2616C52.9906 21.9382 53.1839 22.6801 53.2156 23.4375C53.2857 24.2036 53.2044 24.9761 52.9763 25.7108C52.6315 26.764 51.9906 27.6957 51.1303 28.3943C50.2529 29.1085 49.2331 29.6271 48.1391 29.9155C47.1403 30.1876 46.1053 30.3029 45.071 30.2574C42.8337 30.1925 40.6515 29.5476 38.7383 28.3857C38.3879 28.1721 38.0461 27.9413 37.7128 27.7106C37.5248 27.5824 37.5077 27.4969 37.6444 27.2918C38.368 26.1523 39.1001 25.0299 39.8408 23.9246C39.8802 23.8656 39.9293 23.8135 39.9861 23.7708C40.0073 23.7602 40.0307 23.7548 40.0544 23.7548C40.0781 23.7548 40.1015 23.7602 40.1228 23.7708C40.2424 23.8392 40.3535 23.9246 40.4732 24.0101C41.9194 25.1065 43.6924 25.6845 45.5069 25.651C45.867 25.6611 46.226 25.6061 46.5666 25.4886C46.7681 25.4149 46.9626 25.3234 47.1478 25.2151C47.3263 25.1261 47.4905 25.0109 47.6349 24.8733C47.8668 24.6235 47.9956 24.2953 47.9956 23.9545C47.9956 23.6137 47.8668 23.2856 47.6349 23.0358C47.4491 22.8154 47.2229 22.6326 46.9683 22.4974C46.655 22.3237 46.3326 22.1668 46.0026 22.0274C45.3787 21.7795 44.7463 21.5573 44.1224 21.318C43.4985 21.0787 42.9174 20.8565 42.3192 20.6002C41.876 20.4156 41.45 20.1925 41.0458 19.9335C40.6405 19.6895 40.2623 19.403 39.9177 19.0789C39.1488 18.3425 38.6544 17.3656 38.5161 16.31C38.3582 15.377 38.4357 14.4193 38.7416 13.5239C39.0476 12.6284 39.5723 11.8235 40.2681 11.1822C41.1604 10.3375 42.2461 9.72422 43.4302 9.39609C44.0972 9.19949 44.7863 9.08751 45.4813 9.06279C47.6004 8.97923 49.7052 9.44435 51.5918 10.4131C51.8653 10.5584 52.1217 10.7293 52.3866 10.9002C52.4685 10.978 52.5456 11.0608 52.6173 11.1481Z"
            fill="black"
          />
          <path
            d="M105.047 14.9255C105.047 10.302 105.047 5.67847 105.047 1.05498C105.047 -0.141487 104.919 0.0123445 106.064 0.00379831C107.209 -0.00474789 108.32 0.00379831 109.483 0.00379831H110.124C110.154 0.00486069 110.183 0.0119705 110.21 0.0247C110.237 0.0374295 110.262 0.0555156 110.282 0.0778703C110.302 0.100225 110.317 0.126386 110.326 0.15478C110.336 0.183174 110.34 0.213214 110.337 0.243092C110.337 0.431108 110.337 0.627671 110.337 0.815687C110.337 10.2336 110.337 19.6515 110.337 29.0694C110.337 29.266 110.337 29.454 110.337 29.6506C110.329 29.6956 110.309 29.7376 110.279 29.7722C110.249 29.8067 110.21 29.8326 110.166 29.8471H109.876H105.603H105.321C105.275 29.8358 105.233 29.8111 105.201 29.7761C105.169 29.7411 105.148 29.6974 105.141 29.6506C105.137 29.5794 105.137 29.5081 105.141 29.4369V14.9084L105.047 14.9255Z"
            fill="black"
          />
        </svg>
      );
    case circularRightArrow:
      return (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill="none"
            d="M12 22.0001C17.5228 22.0001 22 17.5229 22 12.0001C22 6.47721 17.5228 2.00006 12 2.00006C6.47715 2.00006 2 6.47721 2 12.0001C2 17.5229 6.47715 22.0001 12 22.0001Z"
            stroke="#0171FE"
            strokeWidth="0.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            fill="none"
            d="M12.75 14.7499L15.75 11.7499L12.75 8.74988"
            stroke="#0171FE"
            strokeWidth="0.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            fill="none"
            d="M6.75 11.7499L15.75 11.7499"
            stroke="#0171FE"
            strokeWidth="0.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case BRAND_DINERS:
      return (
        <svg
          className={classes}
          width="24"
          height="16"
          viewBox="0 0 24 16"
          xmlns="http://www.w3.org/2000/svg"
          {...roleInfo}
        >
          <g fillRule="nonzero" fill="none">
            <path
              d="M21.9972 15.75h.0022c.955.0081 1.7387-.7727 1.7506-1.7458V2.0063c-.0038-.4706-.1911-.9201-.5203-1.2495a1.7197 1.7197 0 0 0-1.2325-.5067H2.0006C1.5411.246 1.0986.4283.7703.7568.441 1.0862.2538 1.5357.25 2.0043v11.9894c.0038.4706.1911.9201.5203 1.2495a1.7197 1.7197 0 0 0 1.2325.5067h19.9944zm-.001.5h-.0011.0021-.001z"
              strokeOpacity=".2"
              stroke="#000"
              strokeWidth=".5"
              fill="#FFF"
            />
            <path
              d="M10.0021 2.0518v-.016h4v.016c3.1354.2294 5.5621 2.8402 5.5621 5.984s-2.4267 5.7546-5.562 5.984v.016h-4v-.016C6.8666 13.7904 4.44 11.1796 4.44 8.0358s2.4267-5.7546 5.5621-5.984z"
              fill="#0165AC"
            />
            <path
              d="M11.6021 11.4278a3.6 3.6 0 0 0 0-6.784v6.784zm-2.4-6.784a3.6 3.6 0 0 0 0 6.784v-6.784zm1.2 8.592c-2.8719 0-5.2-2.3281-5.2-5.2s2.3281-5.2 5.2-5.2 5.2 2.3281 5.2 5.2-2.3281 5.2-5.2 5.2z"
              fill="#FFF"
            />
          </g>
        </svg>
      );
    case LOCATION_LINE:
      return (
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle fill="none" cx="9.5" cy="6.5" r="4.75" stroke="#0171FE" strokeWidth="1.5" />
          <line x1="9.75" y1="11" x2="9.75" y2="20" stroke="#0171FE" strokeWidth="1.5" />
        </svg>
      );
    case USERS:
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M11.3334 14V12.6667C11.3334 11.9594 11.0525 11.2811 10.5524 10.781C10.0523 10.281 9.37399 10 8.66675 10H3.33341C2.62617 10 1.94789 10.281 1.4478 10.781C0.9477 11.2811 0.666748 11.9594 0.666748 12.6667V14"
            stroke="#0171FE"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M5.99992 7.33333C7.47268 7.33333 8.66659 6.13943 8.66659 4.66667C8.66659 3.19391 7.47268 2 5.99992 2C4.52716 2 3.33325 3.19391 3.33325 4.66667C3.33325 6.13943 4.52716 7.33333 5.99992 7.33333Z"
            stroke="#0171FE"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M15.3333 14V12.6667C15.3328 12.0758 15.1362 11.5019 14.7742 11.0349C14.4122 10.5679 13.9053 10.2344 13.3333 10.0867"
            stroke="#0171FE"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M10.6667 2.08667C11.2404 2.23354 11.7488 2.56714 12.1118 3.03488C12.4749 3.50262 12.672 4.07789 12.672 4.67C12.672 5.26212 12.4749 5.83739 12.1118 6.30513C11.7488 6.77287 11.2404 7.10647 10.6667 7.25334"
            stroke="#0171FE"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case CLOKC_LINE:
      return (
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clipPath="url(#clip0_807_3219)">
            <path
              fill="none"
              d="M9.99935 18.3307C14.6017 18.3307 18.3327 14.5998 18.3327 9.9974C18.3327 5.39502 14.6017 1.66406 9.99935 1.66406C5.39698 1.66406 1.66602 5.39502 1.66602 9.9974C1.66602 14.5998 5.39698 18.3307 9.99935 18.3307Z"
              stroke="#0171FE"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              fill="none"
              d="M10 5V10L13.3333 11.6667"
              stroke="#0171FE"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
          <defs>
            <clipPath id="clip0_807_3219">
              <rect width="20" height="20" fill="white" />
            </clipPath>
          </defs>
        </svg>
      );
    case BRAND_DISCOVERY:
      return (
        <svg
          className={classes}
          width="24"
          height="16"
          viewBox="0 0 24 16"
          xmlns="http://www.w3.org/2000/svg"
          {...roleInfo}
        >
          <g fillRule="nonzero" fill="none">
            <path
              d="M21.9972 15.75h.0022c.955.0081 1.7387-.7727 1.7506-1.7458V2.0063c-.0038-.4706-.1911-.9201-.5203-1.2495a1.7197 1.7197 0 0 0-1.2325-.5067H2.0006C1.5411.246 1.0986.4283.7703.7568.441 1.0862.2538 1.5357.25 2.0043v11.9894c.0038.4706.1911.9201.5203 1.2495a1.7197 1.7197 0 0 0 1.2325.5067h19.9944zm-.001.5h-.0011.0021-.001z"
              strokeOpacity=".2"
              stroke="#000"
              strokeWidth=".5"
              fill="#FFF"
            />
            <path
              d="M12.6124 16h9.3848A1.9862 1.9862 0 0 0 24 14.0302v-2.3586a38.7393 38.7393 0 0 1-11.3876 4.3282z"
              fill="#F27712"
            />
            <path
              d="M23.1724 9.2965H22.32l-.96-1.2662h-.091v1.2662h-.6952V6.1517H21.6c.8028 0 1.2662.331 1.2662.9268 0 .4883-.2896.8028-.811.9021l1.1172 1.3159zm-1.0262-2.1931c0-.3062-.2317-.4635-.662-.4635h-.2152v.9518h.1986c.4469 0 .6786-.1656.6786-.4883zm-4.0055-.9517h1.9696v.5296H18.836v.7035h1.2248v.5379h-1.2248v.8524h1.2744v.5297h-1.9696V6.1517zm-2.2345 3.2275L14.4 6.1434h.7614l.9517 2.1186.96-2.1186h.7448l-1.5227 3.2358h-.389zm-6.298-.0082c-1.0592 0-1.8868-.72-1.8868-1.6552 0-.9104.8441-1.647 1.9034-1.647.298 0 .5462.058.8524.1904v.7283a1.2414 1.2414 0 0 0-.869-.3558c-.662 0-1.1668.48-1.1668 1.084 0 .6373.4965 1.0925 1.2 1.0925.3145 0 .5545-.0993.8358-.3476v.7283c-.3144.1324-.5793.182-.869.182zm-2.102-1.0345c0 .6124-.5048 1.0345-1.2331 1.0345-.5297 0-.9103-.1821-1.2331-.5959l.4552-.389c.1572.2814.422.4221.753.4221.3146 0 .538-.1903.538-.4386 0-.1407-.0662-.2483-.2069-.331a2.88 2.88 0 0 0-.48-.1821c-.6538-.207-.8772-.4304-.8772-.869 0-.513.48-.902 1.109-.902.3972 0 .753.124 1.051.3558l-.3642.4138a.7614.7614 0 0 0-.5627-.2483c-.298 0-.5131.149-.5131.3476 0 .1655.1241.2566.5379.3973.7945.2482 1.0262.48 1.0262.993v-.0082zm-3.418-2.1848h.6952v3.153h-.6951v-3.153zm-2.2344 3.153H.8276v-3.153h1.0262c1.1255 0 1.9034.6455 1.9034 1.5724 0 .4717-.2317.9186-.6372 1.2165-.3476.2483-.7366.3642-1.2745.3642h.0083zm.811-2.3668c-.2317-.1821-.4965-.2483-.9517-.2483h-.1903V8.775h.1903c.4469 0 .7283-.0828.9517-.2483.24-.1986.3807-.4965.3807-.8027 0-.3062-.1407-.5959-.3807-.7862z"
              fill="#000"
            />
            <path
              d="M12.4138 6.0689c-.9104 0-1.6552.7283-1.6552 1.6303 0 .96.7117 1.68 1.6552 1.68.9269 0 1.6552-.7282 1.6552-1.6551 0-.927-.72-1.6552-1.6552-1.6552z"
              fill="#F27712"
            />
          </g>
        </svg>
      );
    case BRAND_JCB:
      return (
        <svg
          className={classes}
          height="16"
          viewBox="0 0 23 16"
          width="23"
          xmlns="http://www.w3.org/2000/svg"
          {...roleInfo}
        >
          <g fill="none">
            <path
              d="M.2054 16h4.5178c1.0278 0 2.259-1.1947 2.259-2.1333V0h-4.518C1.4366 0 .2055 1.1947.2055 3.2z"
              fill="#047ab1"
            />
            <path
              d="M2.7692 10.816a9.7709 9.7709 0 0 1-2.5638-.3413V9.3013c.6467.3777 1.386.5909 2.146.6187.8696 0 1.3553-.576 1.3553-1.3653V5.3333h2.146v3.2214c0 1.2586-.7003 2.2613-3.0835 2.2613z"
              fill="#fff"
            />
            <path
              d="M8.1116 16h4.5179c1.0278 0 2.2589-1.1947 2.2589-2.1333V0h-4.5179C9.3427 0 8.1116 1.1947 8.1116 3.2z"
              fill="#d42d06"
            />
            <path
              d="M8.1116 6.08c.6551-.5973 1.7846-.9707 3.6143-.8853.994.0426 2.033.32 2.033.32v1.184c-.5943-.3395-1.2623-.5474-1.954-.608C10.4158 5.9733 9.5687 6.7093 9.5687 8s.8471 2.0267 2.2364 1.92c.692-.066 1.3596-.2774 1.954-.6187v1.1734s-1.0392.288-2.0331.3306c-1.8297.0854-2.9592-.288-3.6143-.8853z"
              fill="#fff"
            />
            <path
              d="M16.0179 16h4.5178c1.0278 0 2.259-1.1947 2.259-2.1333V0h-4.518C17.249 0 16.018 1.1947 16.018 3.2z"
              fill="#67b637"
            />
            <path
              d="M21.6652 9.28c0 .8533-.7003 1.3867-1.6377 1.3867h-4.0096V5.3333h3.6481l.2598.0107c.8245.0427 1.4344.5013 1.4344 1.2907 0 .6186-.4179 1.152-1.1746 1.28v.032c.8358.0533 1.4796.5546 1.4796 1.3333zM18.785 6.176a1.0871 1.0871 0 0 0-.1468-.0107H17.283v1.344h1.5021c.2824-.064.5196-.3093.5196-.672 0-.3626-.2372-.608-.5196-.6613zm.1695 2.176a1.0276 1.0276 0 0 0-.1808-.0107H17.283v1.4614h1.4908l.1808-.0214c.2823-.064.5195-.3413.5195-.7146 0-.3734-.2259-.64-.5195-.7147z"
              fill="#fff"
            />
          </g>
        </svg>
      );
    case BRAND_MASTERCARD:
      return (
        <svg
          className={classes}
          height="16"
          viewBox="0 0 24 16"
          width="24"
          xmlns="http://www.w3.org/2000/svg"
          {...roleInfo}
        >
          <g fill="none" fillRule="evenodd">
            <rect fill="#252525" height="16" rx="2" width="24" />
            <circle cx="9" cy="8" fill="#eb001b" r="5" />
            <circle cx="15" cy="8" fill="#f79e1b" r="5" />
            <path
              d="M12 3.9996c1.2144.9122 2 2.3646 2 4.0004s-.7856 3.0882-2 4.0004C10.7856 11.0882 10 9.6358 10 8s.7856-3.0882 2-4.0004z"
              fill="#ff5f00"
            />
          </g>
        </svg>
      );
    case BRAND_UNIONPAY:
      return (
        <svg
          className={classes}
          height="16"
          viewBox="0 0 24 16"
          width="24"
          xmlns="http://www.w3.org/2000/svg"
          {...roleInfo}
        >
          <g fill="none">
            <path
              d="M4.5459 0h5.7938c.8087 0 1.3117.7257 1.123 1.619L8.7655 14.3675c-.1904.8902-1.0002 1.6165-1.8095 1.6165H1.1627c-.8076 0-1.3118-.7263-1.1231-1.6165L2.7381 1.619C2.9268.7257 3.7361 0 4.546 0"
              fill="#dd2423"
            />
            <path
              d="M9.8576 0h6.6627c.8086 0 .444.7257.2537 1.619l-2.697 12.7485c-.1892.8902-.1302 1.6165-.9405 1.6165H6.4738c-.8103 0-1.3118-.7263-1.1214-1.6165L8.0493 1.619C8.2408.7257 9.0483 0 9.8576 0"
              fill="#16315e"
            />
            <path
              d="M16.256 0h5.7938c.8098 0 1.3129.7257 1.1226 1.619l-2.697 12.7485c-.1903.8902-1.0006 1.6165-1.8104 1.6165h-5.791c-.8104 0-1.313-.7263-1.1232-1.6165L14.4482 1.619C14.637.7257 15.4457 0 16.256 0"
              fill="#036862"
            />
            <path
              d="M6.059 4.0856c-.5958.0067-.7717 0-.828-.0146-.0216.113-.4236 2.1546-.4247 2.1563-.0866.413-.1496.7075-.3635.8976-.1215.1105-.2633.1639-.4276.1639-.2643 0-.4182-.1445-.444-.4184l-.005-.094s.0805-.5533.0805-.5564c0 0 .422-1.8605.4975-2.1064a.1945.1945 0 0 0 .0061-.028c-.8213.008-.967 0-.977-.0146-.0055.02-.0258.1354-.0258.1354l-.4308 2.097-.037.1778-.0716.5817c0 .1725.0307.3134.092.4325.1963.3776.7563.4342 1.073.4342.4081 0 .791-.0955 1.0497-.2698.449-.292.5666-.7486.6713-1.1544l.0486-.2081s.4347-1.9323.5085-2.1837c.0029-.014.004-.0213.0078-.028zm1.479 1.5588c-.1048 0-.2963.028-.4683.1208-.0624.0353-.1215.076-.1838.1166l.0562-.2234-.0307-.0376c-.3648.0813-.4464.0922-.7834.1444l-.0282.0207c-.0391.357-.0739.6255-.219 1.3274a90.3116 90.3116 0 0 1-.17.778l.0156.0327c.3453-.02.45-.02.7502-.0146l.0242-.029c.0382-.215.0431-.2654.1276-.7009.0396-.2064.1224-.66.1632-.8215.075-.0383.149-.0759.2196-.0759.1682 0 .1477.1615.1412.2259-.0072.108-.0685.4609-.1313.7639l-.042.1955c-.0292.1445-.0612.2848-.0905.428l.0127.0286c.3404-.02.4442-.02.7348-.0146l.0342-.029c.0525-.3358.0679-.4257.1611-.9146l.0469-.2246c.091-.4397.1368-.6626.068-.8442-.073-.2035-.2478-.2526-.4083-.2526zm1.6521.4603c-.181.0383-.2963.0637-.411.0802-.1136.02-.2245.0383-.3993.065l-.0139.0138-.0127.011c-.0182.1434-.0309.2673-.055.413-.0205.1506-.052.3218-.1033.5677-.0397.1882-.0601.2538-.0827.32-.022.0663-.0463.1307-.091.3158l.0105.0171.0087.0157c.1634-.0085.2703-.0146.3802-.0157.1097-.0043.2234 0 .3994.001l.0154-.0137.0165-.0153c.0254-.167.0292-.2119.0447-.2933.0154-.0874.042-.2083.107-.5314.0309-.1517.0652-.303.0971-.4578.0332-.1542.068-.306.101-.4578l-.005-.0183zm.0039-.6206c-.1644-.1068-.453-.0729-.6471.0746-.1936.1446-.2157.3498-.0518.458.1615.1038.4512.0729.6436-.0759.1932-.1477.2174-.351.0553-.4567zm.994 2.4734c.3326 0 .6735-.101.9301-.4003.1974-.2428.288-.6041.3193-.7529.1021-.4931.0226-.7233-.0772-.8635-.1517-.2138-.4197-.2824-.6978-.2824-.1672 0-.5654.0182-.8765.334-.2234.2277-.3267.5368-.389.833-.0628.302-.135.8454.319 1.0477.14.0662.342.0844.4722.0844zm-.026-1.1095c.0768-.3734.1673-.6868.3983-.6868.181 0 .1942.2332.1137.6078-.0144.0832-.0805.3924-.1699.524-.0624.0972-.1363.1561-.2179.1561-.0243 0-.1688 0-.171-.2361-.0011-.1166.0205-.2357.0469-.365zm2.1068 1.0615l.026-.0291c.0368-.215.0429-.2655.1245-.7008.0408-.2065.1253-.66.165-.8216.075-.0384.1478-.076.2207-.076.167 0 .1467.1615.14.2259-.006.1082-.0673.4609-.1313.7639l-.0396.1955c-.0304.1446-.0635.2848-.0927.4282l.0127.0286c.3415-.02.4413-.02.7337-.0146l.0353-.0291c.0512-.336.065-.4258.161-.9146l.0458-.2247c.0916-.4397.1379-.6624.07-.844-.0749-.2035-.2509-.2526-.4092-.2526-.1049 0-.2974.0278-.4684.1208a3.0577 3.0577 0 0 0-.1826.1166l.0524-.2234-.028-.0378c-.3647.0814-.448.0923-.7846.1446l-.0258.0206c-.0408.3571-.074.6254-.219 1.3275a95.9633 95.9633 0 0 1-.17.7779l.0155.0328c.3458-.02.449-.02.7486-.0146zm2.5088.0145c.0215-.1154.149-.799.1501-.799 0 0 .1086-.5017.1152-.5199 0 0 .0342-.0522.0684-.0729h.0503c.4743 0 1.01 0 1.4298-.34.2857-.2332.481-.5775.5681-.996a1.6492 1.6492 0 0 0 .0393-.3466c0-.1603-.0292-.3189-.1137-.4428-.214-.3297-.6404-.3358-1.1326-.3383l-.2426.0025c-.63.0086-.8826.0061-.9864-.0079-.0088.0505-.0253.1404-.0253.1404s-.2256 1.1513-.2256 1.1531l-.5655 2.5632c.55-.0073.7756-.0073.8705.0042zm.4181-2.0452s.24-1.149.2388-1.1446l.0078-.059.0034-.0448.0959.0108s.4948.0468.5064.048c.1953.0832.2758.2976.2196.5774-.0513.2558-.202.4708-.3956.5746-.1594.088-.3547.0953-.556.0953h-.1301zm1.494.9905c-.0635.2975-.1364.841.3154 1.0347.144.0674.2731.0875.4043.0802.1385-.0082.2669-.0847.3858-.1948l-.0322.1359.0205.029c.325-.015.4258-.015.7778-.012l.032-.0268c.0513-.3327.0998-.6557.2334-1.2922.065-.3048.13-.6067.1968-.9103l-.0104-.0334c-.3635.0741-.4607.09-.8103.1445l-.0266.0238c-.0035.031-.0072.0607-.0106.0904-.0543-.0967-.1331-.1793-.2547-.2307-.1555-.0674-.5207.0194-.8346.334-.2206.2247-.3265.5325-.3867.8277zm.7633.0182c.0778-.3668.1672-.677.3988-.677.1465 0 .2236.1487.208.4024-.0125.0633-.026.13-.0419.2054-.0232.109-.0483.217-.0727.3252-.0249.074-.0538.1438-.0856.1903-.0596.093-.2014.1506-.283.1506-.0232 0-.166 0-.171-.232-.0011-.1154.0205-.2344.0474-.365zm3.9867-1.2109l-.0281-.0353c-.3597.0802-.4248.093-.7552.1421l-.0243.0267c-.0012.0044-.0021.011-.0038.0171l-.0012-.006c-.246.6248-.2388.49-.439.9818-.0011-.0224-.0011-.0363-.0023-.0601l-.0501-1.0663-.0315-.0353c-.3768.0802-.3857.093-.7336.1421l-.0271.0267c-.0039.0128-.0039.0268-.0061.042l.0022.0055c.0435.2446.033.19.0767.5762.0204.1895.0475.3801.0678.5673.0343.3132.0535.4674.0955.9455-.235.4268-.2907.5884-.5169.963l.0016.0037-.1593.2774c-.0182.0292-.0348.0492-.058.0578-.0253.0138-.0584.0163-.1043.0163h-.0882l-.1312.4804.45.0085c.2642-.0012.4303-.1372.5197-.32l.283-.5339h-.0045l.0298-.0376c.1904-.4511 1.6384-3.1856 1.6384-3.1856zm-4.7501 6.3088h-.191l.7067-2.573h.2344l.0745-.265.0072.2947c-.0088.1822.1214.3437.4634.317h.3956l.1361-.4954h-.1488c-.0856 0-.1253-.0238-.1203-.0748l-.0072-.2999h-.7325v.0016c-.2369.0054-.944.025-1.0873.067-.1732.049-.3558.1937-.3558.1937l.0717-.2654h-.6852l-.1428.5265-.716 2.6123h-.139l-.1363.4919h1.3647l-.0457.164h.6724l.0447-.164h.1886zm-.56-2.0502c-.1097.0334-.3139.1348-.3139.1348l.1816-.6576h.5444l-.1313.4791s-.1682.011-.2807.0437zm.0105.9394s-.171.0237-.2836.0516c-.1108.037-.3187.1536-.3187.1536l.1876-.6843h.5472zm-.305 1.1168h-.5461l.1583-.5775h.5444zm1.315-1.5959h.7872l-.113.4033h-.7977l-.1197.4408h.6979l-.5285.8191c-.037.0602-.0702.0815-.107.0984-.037.0207-.0856.045-.1418.045h-.1936l-.133.4828h.5064c.2632 0 .4187-.1318.5335-.3048l.3624-.546.0778.5544c.0165.1038.0843.1646.1302.1882.0507.028.103.076.177.0832.0794.0037.1368.0067.1749.0067h.2488l.1494-.5404h-.0981c-.0564 0-.1534-.0104-.1699-.0298-.0165-.0237-.0165-.06-.0254-.1154l-.079-.5556h-.3232l.1418-.1858h.796l.1225-.4408h-.737l.1148-.4033h.7347l.1363-.4973h-2.1906zm-6.6482 1.7082l.1837-.6728h.7551l.138-.5004h-.7558l.1153-.4141h.7386l.1369-.4846h-1.848l-.134.4846h.4198l-.112.414h-.421l-.1395.509h.4197l-.2449.8902c-.033.1178.0156.1627.0464.2175.0315.0533.0634.0886.1351.1086.074.0182.1247.0291.1936.0291h.8513l.1517-.5544-.3774.0571c-.0729 0-.2747-.0096-.2526-.0838zm.0866-3.2208l-.1914.3807c-.041.0831-.0778.1348-.111.1585-.0292.02-.0871.0285-.171.0285h-.0998l-.1335.487h.3316c.1594 0 .2819-.0643.3403-.0965.0629-.037.0794-.0159.128-.0675l.112-.1068h1.0354l.1374-.507h-.758l.1324-.277zm1.5286 3.2306c-.0177-.028-.005-.0773.022-.1799l.283-1.0311h1.0068c.1467-.0023.2526-.0042.3215-.0096.074-.0086.1545-.0377.2422-.0899.0905-.0547.1369-.1124.176-.1786.0436-.066.1137-.2107.1738-.4336l.3557-1.3049-1.0447.0067s-.3217.0522-.4633.11c-.143.0643-.3471.244-.3471.244l.0943-.3577h-.6454l-.9035 3.2986c-.032.128-.0535.221-.0585.2769-.0017.0601.069.1197.1147.1646.054.0449.134.0376.2107.0449.0806.0067.1953.0109.3536.0109h.496l.1522-.5659-.444.0462a.1138.1138 0 0 1-.096-.0516zm.4876-1.9074h1.0575l-.0673.2319c-.0094.0054-.032-.0115-.1396.0025h-.9157zm.2118-.778h1.0664l-.0767.2794s-.5026-.0054-.583.0109c-.3543.0675-.5611.2758-.5611.2758zm.8021 1.7866c-.0087.0347-.0226.0558-.042.0717-.0214.0152-.0561.0206-.108.0206h-.1506l.0089-.2824H12.7l-.0254 1.3809c-.001.0996.0078.1573.074.2035.0662.0576.2703.065.545.065h.3927l.1418-.517-.342.0207-.1136.0073c-.0155-.0073-.0304-.014-.0469-.0321-.0144-.0157-.0387-.0061-.0347-.1057l.0027-.354.3585-.0163c.1936 0 .2764-.0694.347-.1354.0674-.0633.0894-.136.1148-.2345l.0602-.3134h-.4927z"
              fill="#fefefe"
            />
          </g>
        </svg>
      );
    case BRAND_VISA:
      return (
        <svg
          className={classes}
          width="24"
          height="16"
          viewBox="0 0 24 16"
          xmlns="http://www.w3.org/2000/svg"
          {...roleInfo}
        >
          <g fillRule="nonzero" fill="none">
            <rect
              strokeOpacity=".2"
              stroke="#000"
              strokeWidth=".5"
              fill="#FFF"
              x=".25"
              y=".25"
              width="23.5"
              height="15.5"
              rx="2"
            />
            <path
              d="M2.78773262 5.91443732C2.26459089 5.62750595 1.6675389 5.39673777 1 5.23659312l.0280005-.12471102h2.73697872c.37098332.01300346.67201191.12462375.76996714.51882925l.59482061 2.83587917.18221641.85413565L6.97796639 5.1118821h1.79882257l-2.67390785 6.1656463H4.30396552l-1.5162329-5.36309108zm7.31224258 5.36963648H8.39882877l1.06401886-6.1721917H11.163901l-1.0639258 6.1721917zm6.1668069-6.02129922l-.2313529 1.3328108-.1538632-.06554092c-.3078194-.12479829-.7141522-.2495093-1.2671852-.2363313-.6716398 0-.9728545.26905813-.9798314.53174543 0 .28895604.3643786.47938251.9593659.76179318.9801104.43993579 1.4347231.97831385 1.4279323 1.68093694-.0137677 1.28044789-1.1758348 2.10786929-2.9607967 2.10786929-.7631763-.0066327-1.4982591-.1579615-1.8972429-.3284901l.2380507-1.38552277.224004.09861682c.5530331.23004776.9169465.32831555 1.5961213.32831555.4897761 0 1.0149017-.19060107 1.0215995-.60409358 0-.26923267-.2237249-.46637906-.8818761-.76842582-.6439184-.29558867-1.5048639-.78797464-1.4909101-1.67447884C11.8780532 5.84042483 13.0610509 5 14.7409877 5c.6580581 0 1.1902536.13788902 1.5257944.26277458zm2.2609703 3.83471102h1.4139786c-.0699548-.30859213-.3921-1.78601186-.3921-1.78601186l-.1188858-.5318327c-.0840015.22978594-.2308878.60409353-.2238179.59091553 0 0-.5390794 1.3723448-.6791749 1.72692903zm2.0998512-3.9856035L22 11.2839865h-1.5750977s-.1541422-.7091684-.2029803-.9258637h-2.1841317c-.0631639.1640705-.3570296.9258637-.3570296.9258637h-1.7849619l2.5268355-5.65999506c.1750729-.40057632.4833574-.51210934.889132-.51210934h1.3158373z"
              fill="#171E6C"
            />
          </g>
        </svg>
      );
    case CLOCK:
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clip-path="url(#clip0_308_2173)">
            <path
              d="M7.99992 14.6667C11.6818 14.6667 14.6666 11.6819 14.6666 8.00001C14.6666 4.31811 11.6818 1.33334 7.99992 1.33334C4.31802 1.33334 1.33325 4.31811 1.33325 8.00001C1.33325 11.6819 4.31802 14.6667 7.99992 14.6667Z"
              stroke="#0171FE"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M8 4V8L10.6667 9.33333"
              stroke="#0171FE"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </g>
          <defs>
            <clipPath id="clip0_308_2173">
              <rect width="16" height="16" fill="white" />
            </clipPath>
          </defs>
        </svg>
      );

    // TrainingProgram icons
    case car:
      return (
        <svg
          width="40"
          height="40"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clip-path="url(#clip0_1210_619)">
            <path
              d="M39.7705 14.6054C39.5749 14.3668 39.283 14.2275 38.9768 14.2275H35.0838C34.1231 11.6935 32.8715 9.21083 31.4489 8.27367C26.5276 5.0361 13.4743 5.0361 8.553 8.27367C7.12936 9.21185 5.88292 11.6976 4.9171 14.2275H1.02413C0.714821 14.2275 0.423949 14.3668 0.230376 14.6054C0.0357786 14.843 -0.0420604 15.1575 0.0214398 15.4596L0.598063 18.2537C0.696385 18.73 1.11631 19.07 1.60075 19.07H2.754C1.64069 20.3544 1.10094 21.9716 1.09377 23.5899C1.0866 25.5892 1.84553 27.3959 3.23332 28.6741C3.24868 28.6875 3.26405 28.6967 3.27839 28.71V32.6194C3.27839 33.4665 3.96665 34.1558 4.81468 34.1558H8.40551C9.25355 34.1558 9.94181 33.4665 9.94181 32.6194V31.0483H30.0581V32.6194C30.0581 33.4665 30.7463 34.1558 31.5943 34.1558H35.1852C36.0312 34.1558 36.7215 33.4665 36.7215 32.6194V28.7868C38.1943 27.4379 38.8979 25.582 38.9071 23.7292C38.9133 22.0556 38.3489 20.379 37.1701 19.068H38.4001C38.8866 19.068 39.3065 18.7279 39.4028 18.2506L39.9805 15.4576C40.0399 15.1575 39.9651 14.8451 39.7705 14.6054ZM10.2399 10.8393C14.1625 8.25728 25.8373 8.25728 29.758 10.8393C30.52 11.3392 31.5001 13.36 32.3983 15.8345H7.60152C8.49769 13.361 9.47785 11.3412 10.2399 10.8393ZM5.6453 23.975C5.6453 22.3055 6.99929 20.9535 8.66771 20.9535C10.3382 20.9535 11.6901 22.3055 11.6901 23.975C11.6901 25.6445 10.3382 26.9975 8.66771 26.9975C6.99929 26.9975 5.6453 25.6445 5.6453 23.975ZM31.3721 26.9975C29.7037 26.9975 28.3497 25.6445 28.3497 23.975C28.3497 22.3055 29.7037 20.9535 31.3721 20.9535C33.0426 20.9535 34.3945 22.3055 34.3945 23.975C34.3945 25.6445 33.0405 26.9975 31.3721 26.9975Z"
              fill="#909ABC"
            />
          </g>
          <defs>
            <clipPath id="clip0_1210_619">
              <rect width="40" height="40" fill="white" />
            </clipPath>
          </defs>
        </svg>
      );

    case aesthetics:
      return (
        <svg
          width="40"
          height="40"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M20.6062 4.88749C21.6312 4.88749 22.4625 5.14374 23.1375 5.53124C24.4375 5.88749 25.975 6.44374 27.475 7.28749C26.35 4.48124 23.6187 2.49374 20.4125 2.49374H19.6C16.4 2.49374 13.6687 4.47499 12.5437 7.26874C16.125 5.24999 19.95 4.91249 20.1687 4.89374C20.3312 4.88124 20.475 4.88124 20.6125 4.88124L20.6062 4.88749Z"
            fill="#909ABC"
          />
          <path
            d="M7.12516 22.3625C7.43141 22.0125 7.87516 21.8062 8.35641 21.8062C8.97516 21.8062 9.54391 22.15 9.83766 22.7C10.1002 23.1812 10.4252 23.6 10.7689 23.9562L10.9002 22.0375C10.9002 22.0375 10.4689 15.6187 21.8314 13.225C22.1127 13.1625 22.3814 13.0625 22.6252 12.9062C23.3502 12.45 24.5752 11.3062 24.2252 8.90624C24.2252 8.90624 23.5627 5.93124 20.2564 6.14999C20.2564 6.14999 13.3814 6.69999 9.74391 11.1625C9.74391 11.1625 5.78141 15.3437 6.88141 21.175C6.88141 21.175 6.95016 21.65 7.12516 22.3625Z"
            fill="#909ABC"
          />
          <path
            d="M33.1188 21.175C34.2188 15.3438 30.2563 11.1625 30.2563 11.1625C28.8063 9.38125 26.8438 8.2375 25 7.4875C25.225 7.90625 25.3688 8.30625 25.4438 8.63125L25.4563 8.71875C25.9313 11.925 24.1 13.4563 23.2938 13.9625C22.9625 14.1688 22.5938 14.3188 22.2 14.4125C29.4125 17.2625 29.1 22.0375 29.1 22.0375L29.3563 25.8063L28.8875 27.45C27.6938 31.4188 23.9563 34.1438 19.825 34.0625C16.3563 34 13.2688 31.9375 11.7375 28.8063C11.3813 29.0813 11.0688 29.3875 10.8 29.6938C11.6813 31.35 12.95 32.7188 14.4625 33.6875V36.5938C14.4625 37.0938 14.8688 37.5 15.3688 37.5H24.3438C24.8375 37.5 25.2438 37.0938 25.2438 36.5938V33.8938C27.4688 32.6063 29.2063 30.5313 30.025 27.975C30.05 27.9625 30.0813 27.9563 30.1063 27.9313C32.45 25.6125 33.1188 21.175 33.1188 21.175Z"
            fill="#909ABC"
          />
          <path
            d="M19.0187 22.6438C19.225 22.3688 19.175 21.975 18.9 21.7688C18.625 21.5625 18.2312 21.6125 18.025 21.8875C17.675 22.35 17.15 22.6125 16.575 22.6125C16 22.6125 15.475 22.35 15.125 21.8875C14.9187 21.6125 14.525 21.5563 14.25 21.7688C13.975 21.975 13.9187 22.3688 14.1312 22.6438C14.7187 23.4188 15.6125 23.8625 16.575 23.8625C17.5375 23.8625 18.4375 23.4188 19.0187 22.6438Z"
            fill="#909ABC"
          />
          <path
            d="M26.5187 22.6438C26.725 22.3688 26.675 21.975 26.4 21.7688C26.125 21.5625 25.7312 21.6125 25.525 21.8875C25.175 22.35 24.65 22.6125 24.075 22.6125C23.5 22.6125 22.975 22.35 22.625 21.8875C22.4187 21.6125 22.025 21.5563 21.75 21.7688C21.475 21.975 21.4187 22.3688 21.6312 22.6438C22.2187 23.4188 23.1125 23.8625 24.075 23.8625C25.0375 23.8625 25.9375 23.4188 26.5187 22.6438Z"
            fill="#909ABC"
          />
          <path
            d="M20.3187 30.05C21.1875 30.05 21.9937 29.7687 22.5375 29.275C22.7937 29.0437 22.8125 28.65 22.5812 28.3937C22.35 28.1375 21.9562 28.1187 21.7 28.35C21.3875 28.6375 20.8687 28.8062 20.3187 28.8062C19.7687 28.8062 19.25 28.6375 18.9375 28.35C18.6812 28.1187 18.2875 28.1375 18.0562 28.3937C17.825 28.65 17.8437 29.0437 18.1 29.275C18.6437 29.7687 19.4562 30.05 20.3187 30.05Z"
            fill="#909ABC"
          />
          <path
            d="M11.9624 26.4437C11.0749 25.9375 9.61241 24.9187 8.73741 23.2875C8.57491 22.9875 8.15616 22.9687 7.99366 23.2687C7.52491 24.1125 6.55616 25.45 4.76241 26.45C4.47491 26.6125 4.46241 27.025 4.75616 27.1812C5.62491 27.6562 7.06241 28.6562 8.01241 30.425C8.16866 30.7187 8.58741 30.7375 8.74991 30.4437C9.25616 29.55 10.2874 28.0687 11.9499 27.175C12.2437 27.0187 12.2624 26.6 11.9749 26.4375L11.9624 26.4437Z"
            fill="#909ABC"
          />
          <path
            d="M35.275 32.7375C34.6438 32.3562 33.7438 31.6875 33.1438 30.6625C32.975 30.375 32.5688 30.3625 32.4 30.65C32.0438 31.2437 31.3875 32.075 30.2688 32.7437C29.9875 32.9125 29.9813 33.3062 30.2625 33.475C30.8813 33.8375 31.775 34.5125 32.4188 35.6187C32.5813 35.9 32.9813 35.9187 33.15 35.6375C33.5313 35.0062 34.2188 34.0875 35.2563 33.4687C35.5375 33.3 35.5563 32.9062 35.275 32.7375Z"
            fill="#909ABC"
          />
        </svg>
      );

    case barman:
    case horecaorbarista:
      return (
        <svg
          width="40"
          height="40"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M15.9999 4H11.9999C11.6333 4 11.3333 4.3 11.3333 4.66667V8.39333L9.52659 10.1933C9.40659 10.32 9.33325 10.4867 9.33325 10.6667V15.3333H18.6666V10.6667C18.6666 10.4867 18.5999 10.32 18.4733 10.1933L16.6666 8.39333V4.66667C16.6666 4.3 16.3666 4 15.9999 4Z"
            fill="#909ABC"
          />
          <path
            d="M26.6666 15.3333V13.28C28.9266 12.96 30.6666 11.0133 30.6666 8.66667V4.66667C30.6666 4.3 30.3666 4 29.9999 4H21.9999C21.6333 4 21.3333 4.3 21.3333 4.66667V8.66667C21.3333 11.0133 23.0733 12.96 25.3333 13.28V15.3333H26.6666Z"
            fill="#909ABC"
          />
          <path
            d="M35.9999 16.6667H3.99992C3.63325 16.6667 3.33325 16.9667 3.33325 17.3333C3.33325 17.7 3.63325 18 3.99992 18H5.33325V33.3333H8.67992C8.82659 31.9333 9.93992 30.82 11.3333 30.68V27.3333H9.33325C8.23325 27.3333 7.33325 26.4333 7.33325 25.3333V23.0333C7.33325 21.36 8.69325 20 10.3666 20H16.2999C17.9733 20 19.3333 21.36 19.3333 23.0333V25.3333C19.3333 26.4333 18.4333 27.3333 17.3333 27.3333H15.3333V30.68C16.7266 30.82 17.8399 31.9333 17.9866 33.3333H22.0133C22.1599 31.9333 23.2733 30.82 24.6666 30.68V27.3333H22.6666C21.5666 27.3333 20.6666 26.4333 20.6666 25.3333V23.0333C20.6666 21.36 22.0266 20 23.6999 20H29.6333C31.3066 20 32.6666 21.36 32.6666 23.0333V25.3333C32.6666 26.4333 31.7666 27.3333 30.6666 27.3333H28.6666V30.68C30.0599 30.82 31.1733 31.9333 31.3199 33.3333H34.6666V18H35.9999C36.3666 18 36.6666 17.7 36.6666 17.3333C36.6666 16.9667 36.3666 16.6667 35.9999 16.6667Z"
            fill="#909ABC"
          />
          <path
            d="M35.9999 34.6667H29.9999V33.64C29.9999 32.7333 29.2666 32 28.3666 32H27.3333V26H30.6666C31.0333 26 31.3333 25.7 31.3333 25.3333V23.0333C31.3333 22.0933 30.5733 21.3333 29.6333 21.3333H23.6999C22.7666 21.3333 21.9999 22.0933 21.9999 23.0333V25.3333C21.9999 25.7 22.2999 26 22.6666 26H25.9999V32H24.9732C24.0666 32 23.3333 32.7333 23.3333 33.64V34.6667H16.6666V33.64C16.6666 32.7333 15.9333 32 15.0333 32H13.9999V26H17.3333C17.6999 26 17.9999 25.7 17.9999 25.3333V23.0333C17.9999 22.0933 17.2399 21.3333 16.2999 21.3333H10.3666C9.43325 21.3333 8.66658 22.0933 8.66658 23.0333V25.3333C8.66658 25.7 8.96659 26 9.33325 26H12.6666V32H11.6399C10.7333 32 9.99992 32.7333 9.99992 33.64V34.6667H3.99992C3.63325 34.6667 3.33325 34.9667 3.33325 35.3333C3.33325 35.7 3.63325 36 3.99992 36H10.6666H15.9999H23.9999H29.3333H35.9999C36.3666 36 36.6666 35.7 36.6666 35.3333C36.6666 34.9667 36.3666 34.6667 35.9999 34.6667Z"
            fill="#909ABC"
          />
        </svg>
      );

    case sewing:
      return (
        <svg
          width="40"
          height="40"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clip-path="url(#clip0_1210_642)">
            <path
              d="M18.7447 2.34383C20.8597 2.34383 22.9006 2.72594 24.8456 3.48281C25.443 2.77133 25.9785 2.14297 26.4399 1.6143C23.9985 0.552576 21.3877 7.62939e-05 18.7446 7.62939e-05C18.0974 7.62939e-05 17.5728 0.524764 17.5728 1.17195C17.5728 1.81914 18.0975 2.34383 18.7447 2.34383Z"
              fill="#909ABC"
            />
            <path
              d="M30.5259 10.321C28.9443 9.20016 28.5662 7.00477 29.6793 5.42453C30.7386 3.92055 32.9054 3.40398 34.5682 4.56797C34.7405 4.68852 37.0182 6.31906 39.0641 9.46406C40.5428 7.0893 40.2797 3.90773 38.1857 1.8143C35.7448 -0.627343 31.8235 -0.582031 29.4282 1.81414C25.6421 5.6 1.28324 36.781 0.248158 38.1065C-0.116138 38.573 -0.0751228 39.2381 0.343393 39.6566C0.761908 40.0751 1.42706 40.1161 1.89347 39.7518C2.9798 38.9035 24.1177 22.3905 33.7957 14.3739C32.8164 12.4517 31.5408 11.0373 30.5259 10.321Z"
              fill="#909ABC"
            />
            <path
              d="M35.9741 33.0835C43.1443 23.0327 39.7401 11.1798 33.2241 6.4879C31.9183 5.64954 30.6478 7.46133 31.8773 8.40602C38.2749 13.1331 39.668 23.7598 34.3047 31.3909C31.7766 29.039 28.5577 26.987 25.0209 26.987C21.6062 26.987 18.8281 29.765 18.8281 33.1797C18.8281 36.5944 21.6062 39.3724 25.0209 39.3724C28.6291 39.3724 31.9019 37.6373 34.462 34.9168C36.5041 37.2209 37.7906 39.3727 37.818 39.4191C38.097 39.8935 38.7857 40.2093 39.4222 39.835C39.9801 39.5068 40.1662 38.7886 39.838 38.2307C39.7135 38.0191 38.2784 35.6147 35.9741 33.0835ZM25.0209 37.0288C22.8985 37.0288 21.1719 35.3021 21.1719 33.1798C21.1719 31.0574 22.8985 29.3308 25.0209 29.3308C27.902 29.3308 30.6382 31.1412 32.8357 33.228C30.6765 35.5541 27.9561 37.0288 25.0209 37.0288Z"
              fill="#909ABC"
            />
          </g>
          <defs>
            <clipPath id="clip0_1210_642">
              <rect width="40" height="40" fill="white" />
            </clipPath>
          </defs>
        </svg>
      );

    case bien_etre_corporel:
    case well_being:
      return (
        <svg
          width="40"
          height="40"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clip-path="url(#clip0_1210_654)">
            <path
              d="M23.5305 18.4974C26.0816 20.7041 26.362 20.9782 27.0312 21.5679C27.6227 21.0467 28.0295 20.6621 30.532 18.4974C35.4804 14.2139 38.75 11.3838 38.75 7.12078C38.75 3.17148 35.9188 0 32.3047 0C29.7859 0 28.0315 1.21539 27.0312 2.18469C26.031 1.21539 24.2766 0 21.7578 0C18.1438 0 15.3125 3.17148 15.3125 7.12078C15.3125 11.3838 18.5821 14.2139 23.5305 18.4974ZM23.5156 9.45312H25.8594V7.10938H28.2031V9.45312H30.5469V11.7969H28.2031V14.1406H25.8594V11.7969H23.5156V9.45312Z"
              fill="#909ABC"
            />
            <path
              d="M34.1391 23.2953L28.2432 28.8486C27.156 29.9369 25.6843 30.5469 24.1187 30.5469H15.3125V28.2031H24.1187C26.3901 28.2031 28.2031 25.8941 28.2031 23.5156H14.7701C13.7493 23.5156 12.7422 23.7834 11.8679 24.2847L0 30.8708L6.03336 40L13.3167 35.2344H26.0597C27.6252 35.2344 29.097 34.6244 30.1945 33.527L40 24.01C40 24.01 39.1443 23.1543 39.1352 23.1454C37.7744 21.8036 35.5209 21.9752 34.1391 23.2953Z"
              fill="#909ABC"
            />
          </g>
          <defs>
            <clipPath id="clip0_1210_654">
              <rect width="40" height="40" fill="white" />
            </clipPath>
          </defs>
        </svg>
      );

    case hairstyle:
    case hairdressing:
      return (
        <svg
          width="40"
          height="40"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clip-path="url(#clip0_1210_659)">
            <path
              d="M31.8812 27.183C30.996 27.183 30.1304 27.3603 29.329 27.7017L27.192 24.555L22.9324 30.6372L25.4684 34.4382C25.6658 35.9176 26.3772 37.279 27.4927 38.2999C31.5431 42.0734 38.3889 39.1328 38.3517 33.5914C38.3518 30.0578 35.4491 27.183 31.8812 27.183ZM29.593 30.2058L29.5858 30.2106L29.5855 30.2102C29.5879 30.2086 29.5905 30.2075 29.593 30.2058ZM31.8812 37.659C29.7646 37.659 27.9696 36.0452 27.7724 33.9765C27.7952 31.4526 29.5123 30.2605 29.5937 30.2054C32.2697 28.4068 36.0384 30.3689 36.0107 33.5916C36.0107 35.8343 34.1582 37.659 31.8812 37.659Z"
              fill="#909ABC"
            />
            <path
              d="M35.0306 0.61936L34.5529 0.247139C34.3007 0.0506506 33.979 -0.0342501 33.6629 0.0124921C33.3466 0.0590782 33.0632 0.233015 32.8784 0.493882L18.8261 20.3318L17.1455 18.1172L18.8844 16.6742C19.4119 16.3005 19.5367 15.57 19.1631 15.0424C18.7894 14.5148 18.0589 14.3901 17.5313 14.7638L15.8001 16.2013L14.6691 14.5907L16.4295 13.1326C16.957 12.759 17.0818 12.0284 16.7082 11.5008C16.3344 10.9733 15.604 10.8486 15.0764 11.2222L13.3238 12.6749L12.1928 11.0643L13.9745 9.59105C14.502 9.21735 14.6268 8.48688 14.2531 7.95929C13.8794 7.43178 13.149 7.30701 12.6214 7.68063L10.8474 9.14845L9.70978 7.52839L11.4993 6.04957C12.0268 5.67587 12.1516 4.94539 11.778 4.41781C11.4043 3.89022 10.6739 3.7656 10.1462 4.13915L8.41052 5.57989C7.88839 4.39385 8.22191 2.9251 9.28902 2.09381C9.79897 1.69654 9.89035 0.960992 9.493 0.451042C9.09565 -0.0589868 8.36018 -0.150208 7.85023 0.247061C5.63244 1.9752 5.16861 5.16521 6.79015 7.44419L17.3602 22.4014L13.6442 27.6474C9.07568 25.7722 4.02362 29.7088 4.83166 34.5804C2.99178 35.1321 1.64819 36.8279 1.64819 38.8294C1.64819 39.4759 2.17227 39.9999 2.8187 39.9999C3.46513 39.9999 3.9892 39.4759 3.9892 38.8294C3.9892 37.843 4.68644 37.0139 5.62222 36.791C8.77353 42.2162 17.2419 40.3231 17.6814 34.0051L36.1145 7.73432C37.7059 5.45292 37.2354 2.33704 35.0306 0.61936ZM11.2248 37.659C8.94778 37.659 7.09534 35.8343 7.09534 33.5915C7.05421 30.4456 10.722 28.4533 13.3882 30.1277C13.5549 30.2458 15.3518 31.5723 15.3536 33.6136C15.3415 35.8445 13.49 37.659 11.2248 37.659Z"
              fill="#909ABC"
            />
          </g>
          <defs>
            <clipPath id="clip0_1210_659">
              <rect width="40" height="40" fill="white" />
            </clipPath>
          </defs>
        </svg>
      );

    case RATING_STAR:
      return (
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9.99984 1.66663L12.5748 6.88329L18.3332 7.72496L14.1665 11.7833L15.1498 17.5166L9.99984 14.8083L4.84984 17.5166L5.83317 11.7833L1.6665 7.72496L7.42484 6.88329L9.99984 1.66663Z"
            fill="#FFC107"
          />
        </svg>
      );

    case EMPTY_HEART:
      return (
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M17.3666 3.84172C16.941 3.41589 16.4356 3.0781 15.8794 2.84763C15.3232 2.61716 14.727 2.49854 14.1249 2.49854C13.5229 2.49854 12.9267 2.61716 12.3705 2.84763C11.8143 3.0781 11.3089 3.41589 10.8833 3.84172L9.99994 4.72506L9.1166 3.84172C8.25686 2.98198 7.0908 2.49898 5.87494 2.49898C4.65908 2.49898 3.49301 2.98198 2.63327 3.84172C1.77353 4.70147 1.29053 5.86753 1.29053 7.08339C1.29053 8.29925 1.77353 9.46531 2.63327 10.3251L3.5166 11.2084L9.99994 17.6917L16.4833 11.2084L17.3666 10.3251C17.7924 9.89943 18.1302 9.39407 18.3607 8.83785C18.5912 8.28164 18.7098 7.68546 18.7098 7.08339C18.7098 6.48132 18.5912 5.88514 18.3607 5.32893C18.1302 4.77271 17.7924 4.26735 17.3666 3.84172Z"
            stroke="white"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      );

    case teachingOnBoard:
      return (
        <svg
          width="40"
          height="36"
          viewBox="0 0 40 36"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M7.29402 23.559C6.22046 23.559 5.17101 23.2406 4.27841 22.6441C3.3858 22.0476 2.69014 21.1998 2.2794 20.208C1.86867 19.2161 1.76131 18.1247 1.9709 17.0718C2.1805 16.0189 2.69763 15.0518 3.4569 14.2928C4.21617 13.5338 5.18347 13.0171 6.23645 12.8079C7.28943 12.5987 8.3808 12.7065 9.37252 13.1176C10.3642 13.5287 11.2118 14.2247 11.8079 15.1176C12.404 16.0104 12.722 17.06 12.7216 18.1335C12.7199 19.5723 12.1475 20.9516 11.1299 21.9688C10.1123 22.986 8.73278 23.5579 7.29402 23.559ZM7.29402 14.8236C6.63938 14.8236 5.99944 15.0178 5.45513 15.3815C4.91082 15.7452 4.48659 16.2621 4.23607 16.8669C3.98555 17.4717 3.92 18.1372 4.04771 18.7793C4.17543 19.4213 4.49067 20.0111 4.95356 20.474C5.41646 20.9369 6.00623 21.2521 6.64829 21.3798C7.29034 21.5076 7.95586 21.442 8.56066 21.1915C9.16547 20.941 9.6824 20.5167 10.0461 19.9724C10.4098 19.4281 10.6039 18.7882 10.6039 18.1335C10.6034 17.2559 10.2545 16.4143 9.63385 15.7937C9.01324 15.1731 8.17168 14.8242 7.29402 14.8236Z"
            fill="#0171FE"
          />
          <path
            d="M13.647 36.0004C13.3662 36.0004 13.0968 35.8888 12.8983 35.6903C12.6997 35.4917 12.5882 35.2224 12.5882 34.9416V32.8239C12.5882 31.4198 12.0304 30.0732 11.0375 29.0804C10.0447 28.0875 8.6981 27.5298 7.29401 27.5298C5.88991 27.5298 4.54333 28.0875 3.55048 29.0804C2.55764 30.0732 1.99986 31.4198 1.99986 32.8239V34.9416C1.99986 35.2224 1.88831 35.4917 1.68974 35.6903C1.49117 35.8888 1.22185 36.0004 0.941032 36.0004C0.660213 36.0004 0.390895 35.8888 0.192326 35.6903C-0.00624272 35.4917 -0.117798 35.2224 -0.117798 34.9416V32.8239C-0.117798 30.8582 0.663087 28.973 2.05307 27.583C3.44305 26.193 5.32828 25.4121 7.29401 25.4121C9.25974 25.4121 11.145 26.193 12.5349 27.583C13.9249 28.973 14.7058 30.8582 14.7058 32.8239V34.9416C14.7058 35.2224 14.5943 35.4917 14.3957 35.6903C14.1971 35.8888 13.9278 36.0004 13.647 36.0004Z"
            fill="#0171FE"
          />
          <path
            d="M13.647 34.645C13.4456 34.6448 13.2484 34.5872 13.0785 34.479C12.9087 34.3707 12.7733 34.2163 12.6881 34.0338C12.603 33.8513 12.5717 33.6482 12.5978 33.4486C12.624 33.2489 12.7066 33.0608 12.8359 32.9064L27.6595 15.2027C27.8428 14.998 28.0986 14.8726 28.3727 14.853C28.6468 14.8334 28.9177 14.9212 29.1283 15.0977C29.339 15.2742 29.4727 15.5257 29.5013 15.799C29.5299 16.0723 29.4511 16.346 29.2816 16.5623L14.458 34.2659C14.3587 34.3845 14.2346 34.4799 14.0945 34.5454C13.9544 34.6109 13.8016 34.6449 13.647 34.645Z"
            fill="#0171FE"
          />
          <path
            d="M5.17638 10.5883C4.89556 10.5883 4.62625 10.4767 4.42768 10.2782C4.22911 10.0796 4.11755 9.81028 4.11755 9.52946V1.05883C4.11755 0.77801 4.22911 0.508693 4.42768 0.310124C4.62625 0.111555 4.89556 0 5.17638 0C5.4572 0 5.72652 0.111555 5.92509 0.310124C6.12366 0.508693 6.23521 0.77801 6.23521 1.05883V9.52946C6.23521 9.81028 6.12366 10.0796 5.92509 10.2782C5.72652 10.4767 5.4572 10.5883 5.17638 10.5883Z"
            fill="#0171FE"
          />
          <path
            d="M39.0589 2.11766H3.05871C2.77789 2.11766 2.50857 2.0061 2.31 1.80753C2.11143 1.60897 1.99988 1.33965 1.99988 1.05883C1.99988 0.77801 2.11143 0.508693 2.31 0.310124C2.50857 0.111555 2.77789 0 3.05871 0H39.0589C39.3397 0 39.609 0.111555 39.8076 0.310124C40.0062 0.508693 40.1177 0.77801 40.1177 1.05883C40.1177 1.33965 40.0062 1.60897 39.8076 1.80753C39.609 2.0061 39.3397 2.11766 39.0589 2.11766Z"
            fill="#0171FE"
          />
          <path
            d="M36.9413 25.4119C36.6605 25.4119 36.3911 25.3004 36.1926 25.1018C35.994 24.9032 35.8824 24.6339 35.8824 24.3531V1.05883C35.8824 0.77801 35.994 0.508693 36.1926 0.310124C36.3911 0.111555 36.6605 0 36.9413 0C37.2221 0 37.4914 0.111555 37.69 0.310124C37.8886 0.508693 38.0001 0.77801 38.0001 1.05883V24.3531C38.0001 24.6339 37.8886 24.9032 37.69 25.1018C37.4914 25.3004 37.2221 25.4119 36.9413 25.4119Z"
            fill="#0171FE"
          />
          <path
            d="M39.0589 25.4121H26.3529C26.0721 25.4121 25.8028 25.3005 25.6042 25.102C25.4056 24.9034 25.2941 24.6341 25.2941 24.3533C25.2941 24.0724 25.4056 23.8031 25.6042 23.6046C25.8028 23.406 26.0721 23.2944 26.3529 23.2944H39.0589C39.3397 23.2944 39.609 23.406 39.8076 23.6046C40.0061 23.8031 40.1177 24.0724 40.1177 24.3533C40.1177 24.6341 40.0061 24.9034 39.8076 25.102C39.609 25.3005 39.3397 25.4121 39.0589 25.4121Z"
            fill="#0171FE"
          />
          <path
            d="M30.5883 8.47069H17.8823C17.6015 8.47069 17.3322 8.35913 17.1336 8.16056C16.935 7.96199 16.8235 7.69268 16.8235 7.41186C16.8235 7.13104 16.935 6.86172 17.1336 6.66315C17.3322 6.46458 17.6015 6.35303 17.8823 6.35303H30.5883C30.8691 6.35303 31.1384 6.46458 31.337 6.66315C31.5355 6.86172 31.6471 7.13104 31.6471 7.41186C31.6471 7.69268 31.5355 7.96199 31.337 8.16056C31.1384 8.35913 30.8691 8.47069 30.5883 8.47069Z"
            fill="#0171FE"
          />
          <path
            d="M30.5882 12.706H13.647C13.3661 12.706 13.0968 12.5945 12.8983 12.3959C12.6997 12.1973 12.5881 11.928 12.5881 11.6472C12.5881 11.3664 12.6997 11.0971 12.8983 10.8985C13.0968 10.6999 13.3661 10.5884 13.647 10.5884H30.5882C30.8691 10.5884 31.1384 10.6999 31.3369 10.8985C31.5355 11.0971 31.6471 11.3664 31.6471 11.6472C31.6471 11.928 31.5355 12.1973 31.3369 12.3959C31.1384 12.5945 30.8691 12.706 30.5882 12.706Z"
            fill="#0171FE"
          />
        </svg>
      );

    case chooseFingerPoint:
      return (
        <svg
          width="40"
          height="40"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clip-path="url(#clip0_1068_1498)">
            <path
              d="M6.47815 18.7905C6.04856 19.4743 5.89974 20.2976 6.06279 21.0885C6.22584 21.8794 6.68814 22.5768 7.35315 23.0349L8.60475 23.9148C9.23925 24.2433 9.80015 24.6977 10.2532 25.2502C10.7063 25.8027 11.042 26.4417 11.2399 27.1283C11.8305 28.999 12.5658 31.3276 14.5407 32.6313C15.3469 33.2721 16.3409 33.8594 16.3245 35.0004L16.323 39.4612C16.3241 39.6521 16.4004 39.8349 16.5354 39.9698C16.6703 40.1048 16.853 40.1812 17.0439 40.1824H29.1393C29.3305 40.1824 29.5138 40.1065 29.649 39.9713C29.7842 39.8361 29.8601 39.6528 29.8601 39.4616V35.804C29.8557 35.34 30.0086 34.8881 30.2937 34.5221C32.5717 31.6873 33.8718 28.1919 34 24.5575V18.8595C33.993 18.3226 33.8426 17.7973 33.5645 17.3379C33.2864 16.8786 32.8906 16.5019 32.4181 16.2468C31.9456 15.9917 31.4135 15.8675 30.8769 15.887C30.3403 15.9065 29.8186 16.069 29.3659 16.3578C29.2468 15.9044 29.0236 15.4851 28.714 15.1331C28.4045 14.7811 28.0171 14.5062 27.5827 14.3301C27.1483 14.154 26.6788 14.0817 26.2115 14.1188C25.7443 14.156 25.2921 14.3016 24.891 14.5441C24.7913 13.8273 24.4359 13.1708 23.8902 12.6955C23.3445 12.2203 22.6454 11.9584 21.9218 11.9581C21.376 11.9567 20.8403 12.1059 20.3738 12.3892V7.82809C20.3725 7.41962 20.2877 7.01575 20.1247 6.64121C19.9617 6.26668 19.7239 5.92939 19.4259 5.65003C19.1279 5.37067 18.776 5.15511 18.3918 5.01659C18.0075 4.87807 17.599 4.8195 17.1913 4.84448C16.4223 4.9048 15.7052 5.25572 15.1857 5.8259C14.6662 6.39607 14.3833 7.1427 14.3946 7.91397V20.0058C13.3295 19.2903 12.1047 18.5251 10.7352 17.7195C10.0257 17.309 9.18422 17.1913 8.38937 17.3913C7.59452 17.5913 6.90892 18.0932 6.47815 18.7905ZM14.6952 21.9681C14.8034 22.0435 14.93 22.088 15.0616 22.0968C15.1931 22.1057 15.3246 22.0786 15.4419 22.0184C15.5592 21.9582 15.6579 21.8671 15.7273 21.7551C15.7968 21.6431 15.8345 21.5142 15.8363 21.3824V7.91397C15.8274 7.70873 15.8594 7.50377 15.9304 7.31102C16.0015 7.11826 16.1102 6.94157 16.2502 6.79124C16.3902 6.6409 16.5587 6.51992 16.7459 6.43534C16.9332 6.35075 17.1353 6.30426 17.3407 6.29857C17.546 6.29288 17.7505 6.3281 17.9421 6.40218C18.1337 6.47625 18.3087 6.58771 18.4568 6.73006C18.6049 6.87241 18.7232 7.04281 18.8048 7.23133C18.8865 7.41985 18.9298 7.62272 18.9323 7.82814V21.3109C18.9359 21.4997 19.0135 21.6795 19.1483 21.8116C19.2831 21.9438 19.4643 22.0178 19.6531 22.0178C19.8419 22.0178 20.0231 21.9438 20.1579 21.8116C20.2927 21.6794 20.3702 21.4996 20.3739 21.3109V14.9477C20.3752 14.5371 20.5392 14.1437 20.83 13.8538C21.1207 13.5639 21.5146 13.4011 21.9252 13.4011C22.3358 13.4011 22.7297 13.5639 23.0205 13.8538C23.3113 14.1437 23.4753 14.5371 23.4765 14.9477V21.3109C23.4802 21.4997 23.5578 21.6795 23.6926 21.8116C23.8273 21.9438 24.0086 22.0178 24.1974 22.0178C24.3862 22.0178 24.5674 21.9438 24.7022 21.8116C24.837 21.6794 24.9145 21.4996 24.9182 21.3109V17.0859C24.9191 16.676 25.0826 16.2831 25.3728 15.9935C25.663 15.7039 26.0562 15.5413 26.4661 15.5413C26.8761 15.5413 27.2693 15.7039 27.5595 15.9935C27.8497 16.2831 28.0132 16.676 28.0141 17.0859V21.3109C28.0141 21.5021 28.09 21.6855 28.2252 21.8206C28.3604 21.9558 28.5438 22.0318 28.7349 22.0318C28.9261 22.0318 29.1095 21.9558 29.2446 21.8206C29.3798 21.6855 29.4558 21.5021 29.4558 21.3109V18.8595C29.4627 18.4527 29.6292 18.0649 29.9194 17.7796C30.2096 17.4944 30.6002 17.3346 31.0071 17.3346C31.414 17.3346 31.8046 17.4944 32.0948 17.7797C32.3849 18.0649 32.5514 18.4528 32.5584 18.8596L32.5591 24.5297C32.4364 27.8454 31.2477 31.0331 29.1696 33.6197C28.0816 34.7743 28.4873 37.3019 28.4174 38.7408H17.7661V35.0008C17.7675 34.5562 17.6796 34.116 17.5076 33.7061C17.3357 33.2962 17.0832 32.925 16.7651 32.6144C16.325 32.1937 15.8565 31.8038 15.3629 31.4473C13.7868 30.4062 13.1641 28.4341 12.6112 26.6831C12.3632 25.8422 11.9517 25.0585 11.4005 24.3769C10.8492 23.6953 10.1688 23.1291 9.39845 22.7107L8.17677 21.8516C7.81578 21.6034 7.5649 21.225 7.47671 20.7959C7.38852 20.3668 7.46988 19.9202 7.70373 19.5497C7.93976 19.175 8.31084 18.9053 8.74016 18.7965C9.16949 18.6878 9.6242 18.7483 10.0102 18.9654C11.8552 20.0509 13.4316 21.061 14.6952 21.9681Z"
              fill="#0171FE"
            />
            <path
              d="M18.1051 2.41999V0.536608C18.1015 0.3478 18.024 0.167937 17.8892 0.0356821C17.7544 -0.0965727 17.5731 -0.17066 17.3843 -0.170654C17.1954 -0.170649 17.0141 -0.0965505 16.8793 0.0357123C16.7445 0.167975 16.667 0.347843 16.6635 0.536651V2.41999C16.6671 2.60873 16.7447 2.7885 16.8795 2.92068C17.0143 3.05286 17.1955 3.12691 17.3843 3.12691C17.5731 3.12691 17.7543 3.05286 17.8891 2.92068C18.0239 2.7885 18.1014 2.60873 18.1051 2.41999Z"
              fill="#0171FE"
            />
            <path
              d="M22.1218 2.11385L20.79 3.44569C20.7229 3.51257 20.6696 3.59204 20.6332 3.67954C20.5969 3.76703 20.5781 3.86084 20.578 3.9556C20.5779 4.05036 20.5965 4.1442 20.6327 4.23176C20.669 4.31932 20.7221 4.39888 20.7891 4.46588C20.8561 4.53288 20.9357 4.58602 21.0232 4.62224C21.1108 4.65847 21.2046 4.67707 21.2994 4.67699C21.3941 4.6769 21.488 4.65814 21.5755 4.62176C21.663 4.58539 21.7424 4.53211 21.8093 4.46499L23.1411 3.13314C23.272 2.99709 23.3442 2.81515 23.3424 2.62642C23.3405 2.43768 23.2647 2.2572 23.1312 2.12374C22.9978 1.99028 22.8173 1.91448 22.6286 1.91263C22.4398 1.91078 22.2579 1.98303 22.1218 2.11385Z"
              fill="#0171FE"
            />
            <path
              d="M22.2618 7.80945C22.2619 8.00063 22.3378 8.18397 22.473 8.31915C22.6082 8.45433 22.7915 8.53028 22.9827 8.53029H24.8661C25.0572 8.53029 25.2406 8.45434 25.3758 8.31916C25.5109 8.18398 25.5869 8.00063 25.5869 7.80945C25.5869 7.61828 25.5109 7.43493 25.3758 7.29975C25.2406 7.16457 25.0572 7.08862 24.8661 7.08862H22.9827C22.7915 7.08862 22.6081 7.16457 22.473 7.29975C22.3378 7.43493 22.2618 7.61828 22.2618 7.80945Z"
              fill="#0171FE"
            />
            <path
              d="M13.5303 4.82421C13.6718 4.82197 13.8096 4.77838 13.9267 4.6988C14.0438 4.61922 14.135 4.50714 14.1892 4.37635C14.2433 4.24556 14.2581 4.10178 14.2315 3.96273C14.205 3.82367 14.1384 3.69541 14.0399 3.59375L12.7081 2.26189C12.5729 2.12672 12.3896 2.05078 12.1984 2.05078C12.0073 2.05078 11.8239 2.12672 11.6888 2.26189C11.5536 2.39705 11.4777 2.58038 11.4777 2.77153C11.4777 2.96269 11.5536 3.14602 11.6888 3.28118L13.0206 4.61304C13.0875 4.68008 13.1669 4.73324 13.2544 4.76949C13.3418 4.80573 13.4356 4.82432 13.5303 4.82421Z"
              fill="#0171FE"
            />
            <path
              d="M9.39075 8.01844C9.39075 8.20962 9.46669 8.39296 9.60187 8.52814C9.73706 8.66333 9.9204 8.73927 10.1116 8.73927H11.995C12.1861 8.73927 12.3695 8.66333 12.5047 8.52814C12.6398 8.39296 12.7158 8.20962 12.7158 8.01844C12.7158 7.82726 12.6398 7.64392 12.5047 7.50873C12.3695 7.37355 12.1861 7.29761 11.995 7.29761H10.1116C9.92041 7.29762 9.73706 7.37356 9.60188 7.50874C9.4667 7.64392 9.39076 7.82727 9.39075 8.01844Z"
              fill="#0171FE"
            />
          </g>
          <defs>
            <clipPath id="clip0_1068_1498">
              <rect width="40" height="40" fill="white" />
            </clipPath>
          </defs>
        </svg>
      );

    case tvRecordingCamera:
      return (
        <svg
          width="40"
          height="40"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M36.4273 31.5626H3.57266C2.62537 31.5615 1.71718 31.1847 1.04738 30.5149C0.377591 29.845 0.000909895 28.9367 0 27.9894L0 7.56069C0.00107523 6.6135 0.377824 5.7054 1.04759 5.03563C1.71736 4.36586 2.62546 3.98911 3.57266 3.98804H20.6481C20.8553 3.98804 21.054 4.07035 21.2006 4.21686C21.3471 4.36337 21.4294 4.56209 21.4294 4.76929C21.4294 4.97649 21.3471 5.1752 21.2006 5.32171C21.054 5.46823 20.8553 5.55054 20.6481 5.55054H3.57266C3.03971 5.55112 2.52875 5.76309 2.1519 6.13994C1.77505 6.51679 1.56308 7.02774 1.5625 7.56069V27.9894C1.56295 28.5225 1.77487 29.0336 2.15174 29.4105C2.5286 29.7875 3.03963 29.9995 3.57266 30.0001H36.4273C36.9604 29.9995 37.4714 29.7875 37.8483 29.4105C38.2251 29.0336 38.437 28.5225 38.4375 27.9894V12.2501C38.4375 12.0429 38.5198 11.8442 38.6663 11.6976C38.8128 11.5511 39.0116 11.4688 39.2188 11.4688C39.4259 11.4688 39.6247 11.5511 39.7712 11.6976C39.9177 11.8442 40 12.0429 40 12.2501V27.9894C39.9991 28.9367 39.6224 29.845 38.9526 30.5149C38.2828 31.1847 37.3746 31.5615 36.4273 31.5626Z"
            fill="#0171FE"
          />
          <path
            d="M26.1806 38.5888C26.0495 38.589 25.9204 38.5562 25.8053 38.4934C25.6901 38.4305 25.5927 38.3398 25.5219 38.2294L21.0219 31.2025C20.9101 31.028 20.8723 30.8163 20.9167 30.6139C20.9611 30.4115 21.0841 30.235 21.2586 30.1233C21.4331 30.0116 21.6448 29.9738 21.8472 30.0181C22.0496 30.0625 22.2261 30.1855 22.3378 30.36L26.837 37.3867C26.9127 37.5047 26.9553 37.641 26.9602 37.7811C26.9652 37.9212 26.9323 38.0601 26.8651 38.1831C26.7979 38.3062 26.6988 38.4089 26.5782 38.4804C26.4576 38.552 26.32 38.5898 26.1798 38.5899L26.1806 38.5888Z"
            fill="#0171FE"
          />
          <path
            d="M13.8193 38.5886C13.6791 38.5886 13.5415 38.5508 13.4209 38.4792C13.3004 38.4077 13.2013 38.305 13.1341 38.1819C13.0669 38.0589 13.034 37.92 13.0389 37.7799C13.0439 37.6397 13.0864 37.5035 13.1622 37.3855L17.6622 30.3588C17.7739 30.1843 17.9503 30.0613 18.1527 30.0169C18.3551 29.9725 18.5669 30.0104 18.7414 30.1221C18.9159 30.2338 19.0388 30.4103 19.0832 30.6127C19.1276 30.8151 19.0898 31.0268 18.9781 31.2013L14.4781 38.2286C14.4073 38.3391 14.3099 38.43 14.1948 38.493C14.0797 38.5559 13.9505 38.5888 13.8193 38.5886Z"
            fill="#0171FE"
          />
          <path
            d="M29.5997 38.6707H10.4005C10.1933 38.6707 9.9946 38.5883 9.84809 38.4418C9.70157 38.2953 9.61926 38.0966 9.61926 37.8894C9.61926 37.6822 9.70157 37.4835 9.84809 37.337C9.9946 37.1905 10.1933 37.1082 10.4005 37.1082H29.5997C29.8069 37.1082 30.0056 37.1905 30.1522 37.337C30.2987 37.4835 30.381 37.6822 30.381 37.8894C30.381 38.0966 30.2987 38.2953 30.1522 38.4418C30.0056 38.5883 29.8069 38.6707 29.5997 38.6707Z"
            fill="#0171FE"
          />
          <path
            d="M19.9107 18.3778C19.0504 18.3778 18.2095 18.1227 17.4942 17.6448C16.7789 17.1668 16.2214 16.4875 15.8921 15.6927C15.5629 14.8979 15.4768 14.0233 15.6446 13.1796C15.8124 12.3358 16.2267 11.5608 16.835 10.9525C17.4433 10.3441 18.2184 9.92988 19.0621 9.76205C19.9059 9.59421 20.7805 9.68035 21.5753 10.0096C22.3701 10.3388 23.0494 10.8963 23.5274 11.6116C24.0053 12.3269 24.2604 13.1679 24.2604 14.0282C24.2591 15.1814 23.8004 16.287 22.985 17.1024C22.1696 17.9179 21.0639 18.3766 19.9107 18.3778ZM19.9107 11.241C19.3595 11.241 18.8206 11.4044 18.3622 11.7107C17.9039 12.017 17.5467 12.4523 17.3357 12.9615C17.1247 13.4708 17.0695 14.0312 17.1771 14.5719C17.2846 15.1126 17.5501 15.6092 17.9399 15.999C18.3297 16.3888 18.8263 16.6542 19.367 16.7618C19.9076 16.8693 20.468 16.8141 20.9773 16.6032C21.4866 16.3922 21.9219 16.035 22.2282 15.5766C22.5344 15.1183 22.6979 14.5794 22.6979 14.0282C22.6971 13.2892 22.4032 12.5808 21.8807 12.0582C21.3581 11.5357 20.6497 11.2418 19.9107 11.241Z"
            fill="#0171FE"
          />
          <path
            d="M25.2634 25.8715H14.5582C14.351 25.8715 14.1523 25.7892 14.0058 25.6427C13.8593 25.4962 13.777 25.2975 13.777 25.0903V22.9496C13.7852 21.3282 14.435 19.776 15.5844 18.6323C16.7339 17.4887 18.2894 16.8467 19.9108 16.8467C21.5323 16.8467 23.0877 17.4887 24.2372 18.6323C25.3866 19.776 26.0365 21.3282 26.0446 22.9496V25.0903C26.0446 25.2975 25.9623 25.4962 25.8158 25.6427C25.6693 25.7892 25.4706 25.8715 25.2634 25.8715ZM15.3395 24.309H24.4821V22.9496C24.4751 21.7419 23.9903 20.586 23.1338 19.7344C22.2773 18.8829 21.1186 18.4049 19.9108 18.4049C18.703 18.4049 17.5443 18.8829 16.6878 19.7344C15.8313 20.586 15.3465 21.7419 15.3395 22.9496V24.309Z"
            fill="#0171FE"
          />
          <path
            d="M34.6905 11.2343H24.967C24.5114 11.2338 24.0745 11.0525 23.7522 10.7303C23.43 10.4081 23.2488 9.9712 23.2483 9.51551V3.04785C23.2488 2.59216 23.43 2.15528 23.7522 1.83306C24.0745 1.51084 24.5114 1.3296 24.967 1.3291H34.6905C35.1462 1.3296 35.583 1.51084 35.9053 1.83306C36.2275 2.15528 36.4087 2.59216 36.4092 3.04785V9.51551C36.4087 9.9712 36.2275 10.4081 35.9053 10.7303C35.583 11.0525 35.1462 11.2338 34.6905 11.2343ZM24.967 2.8916C24.9256 2.8916 24.8859 2.90806 24.8566 2.93737C24.8273 2.96667 24.8108 3.00641 24.8108 3.04785V9.51551C24.8108 9.55695 24.8273 9.59669 24.8566 9.62599C24.8859 9.6553 24.9256 9.67176 24.967 9.67176H34.6905C34.7319 9.67176 34.7717 9.6553 34.801 9.62599C34.8303 9.59669 34.8467 9.55695 34.8467 9.51551V3.04785C34.8467 3.00641 34.8303 2.96667 34.801 2.93737C34.7717 2.90806 34.7319 2.8916 34.6905 2.8916H24.967Z"
            fill="#0171FE"
          />
          <path
            d="M39.2187 9.69797C39.0946 9.69801 38.9723 9.66844 38.862 9.61172L35.2711 7.76797C35.1433 7.70234 35.036 7.60275 34.9612 7.48013C34.8863 7.35752 34.8467 7.21663 34.8467 7.07297V5.39781C34.8467 5.2514 34.8878 5.10794 34.9654 4.98379C35.0431 4.85963 35.154 4.75978 35.2856 4.69563L38.8765 2.94563C38.9957 2.88757 39.1277 2.86078 39.26 2.86779C39.3924 2.87481 39.5208 2.91538 39.6332 2.98571C39.7456 3.05604 39.8382 3.15379 39.9023 3.26979C39.9665 3.38578 40.0001 3.51619 40 3.64875V8.91672C40 9.12392 39.9177 9.32263 39.7711 9.46915C39.6246 9.61566 39.4259 9.69797 39.2187 9.69797ZM36.4092 6.59625L38.4375 7.6375V4.89797L36.4092 5.88625V6.59625Z"
            fill="#0171FE"
          />
        </svg>
      );
    case ideaBulb:
      return (
        <svg
          width="40"
          height="40"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clip-path="url(#clip0_1068_1520)">
            <path
              d="M22.5 39.9999H17.5C16.3333 39.9999 15 38.9999 15 36.9999V33.4999C15 31.8333 14.1666 30.3333 12.8333 29.1666C9.83331 26.8333 8.33331 23.4999 8.33331 19.8333C8.49998 13.4999 13.6666 8.49992 19.8333 8.33325C23 8.33325 26 9.49992 28.1666 11.6666C30.3333 13.8333 31.6666 16.8333 31.6666 19.9999C31.6666 23.4999 30.1666 26.8333 27.3333 28.9999C25.8333 30.1666 25 31.9999 25 33.6666V37.4999C25 38.8333 23.8333 39.9999 22.5 39.9999ZM20 9.99992C14.6666 9.99992 10.1666 14.4999 9.99998 19.8333C9.99998 22.9999 11.3333 25.9999 13.8333 27.8333C15.6666 29.3333 16.6666 31.3333 16.6666 33.4999V36.9999C16.6666 37.3333 16.6666 38.3333 17.5 38.3333H22.5C23 38.3333 23.3333 37.9999 23.3333 37.4999V33.6666C23.3333 31.4999 24.5 29.1666 26.3333 27.6666C28.6666 25.8333 30 22.9999 30 19.9999C30 17.3333 29 14.8333 27 12.8333C25.1666 10.9999 22.6666 9.99992 20 9.99992Z"
              fill="#0171FE"
            />
            <path
              d="M24.1667 34.9999H15.8333C15.3333 34.9999 15 34.6666 15 34.1666C15 33.6666 15.3333 33.3333 15.8333 33.3333H24.1667C24.6667 33.3333 25 33.6666 25 34.1666C25 34.6666 24.6667 34.9999 24.1667 34.9999Z"
              fill="#0171FE"
            />
            <path
              d="M20 5C19.5 5 19.1667 4.66667 19.1667 4.16667V0.833333C19.1667 0.333333 19.5 0 20 0C20.5 0 20.8334 0.333333 20.8334 0.833333V4.16667C20.8334 4.66667 20.5 5 20 5Z"
              fill="#0171FE"
            />
            <path
              d="M31.1667 9.66667C31 9.66667 30.6667 9.66667 30.5 9.5C30.1667 9.16667 30.1667 8.66667 30.5 8.33333L32.8333 6C33.1667 5.66667 33.6667 5.66667 34 6C34.3333 6.33333 34.3333 6.83333 34 7.16667L31.6667 9.5C31.6667 9.5 31.3333 9.66667 31.1667 9.66667Z"
              fill="#0171FE"
            />
            <path
              d="M39.1667 20.8334H35.8333C35.3333 20.8334 35 20.5001 35 20.0001C35 19.5001 35.3333 19.1667 35.8333 19.1667H39.1667C39.6667 19.1667 40 19.5001 40 20.0001C40 20.5001 39.6667 20.8334 39.1667 20.8334Z"
              fill="#0171FE"
            />
            <path
              d="M33.5 34.3334C33.3333 34.3334 33 34.3334 32.8333 34.1667L30.5 31.8334C30.1667 31.5001 30.1667 31.0001 30.5 30.6667C30.8333 30.3334 31.3333 30.3334 31.6667 30.6667L34 33.0001C34.3333 33.3334 34.3333 33.8334 34 34.1667C34 34.3334 33.8333 34.3334 33.5 34.3334Z"
              fill="#0171FE"
            />
            <path
              d="M6.49998 34.3334C6.33331 34.3334 5.99998 34.3334 5.83331 34.1667C5.49998 33.8334 5.49998 33.3334 5.83331 33.0001L8.16665 30.6667C8.49998 30.3334 8.99998 30.3334 9.33331 30.6667C9.66665 31.0001 9.66665 31.5001 9.33331 31.8334L6.99998 34.1667C6.83331 34.3334 6.66665 34.3334 6.49998 34.3334Z"
              fill="#0171FE"
            />
            <path
              d="M4.16667 20.8334H0.833333C0.333333 20.8334 0 20.5001 0 20.0001C0 19.5001 0.333333 19.1667 0.833333 19.1667H4.16667C4.66667 19.1667 5 19.5001 5 20.0001C5 20.5001 4.66667 20.8334 4.16667 20.8334Z"
              fill="#0171FE"
            />
            <path
              d="M8.83331 9.66659C8.66665 9.66659 8.33331 9.66659 8.16665 9.49992L5.83331 6.99992C5.49998 6.66658 5.49998 6.16659 5.83331 5.83325C6.16665 5.49992 6.66665 5.49992 6.99998 5.83325L9.33331 8.16659C9.66665 8.49992 9.66665 8.99992 9.33331 9.33325C9.16665 9.49992 8.99998 9.66659 8.83331 9.66659Z"
              fill="#0171FE"
            />
            <path
              d="M26.6667 20.8333C26.1667 20.8333 25.8334 20.5 25.8334 20C25.8334 16.8333 23.1667 14.1667 20 14.1667C19.5 14.1667 19.1667 13.8333 19.1667 13.3333C19.1667 12.8333 19.5 12.5 20 12.5C24.1667 12.5 27.5 15.8333 27.5 20C27.5 20.5 27.1667 20.8333 26.6667 20.8333Z"
              fill="#0171FE"
            />
          </g>
          <defs>
            <clipPath id="clip0_1068_1520">
              <rect width="40" height="40" fill="white" />
            </clipPath>
          </defs>
        </svg>
      );
    case createUserPlus:
      return (
        <svg
          width="40"
          height="40"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M16.2264 19.245C11.5094 19.245 7.54718 15.2827 7.54718 10.5657C7.54718 5.84874 11.5094 1.88647 16.2264 1.88647C20.9434 1.88647 24.9057 5.84874 24.9057 10.5657C24.9057 15.2827 20.9434 19.245 16.2264 19.245ZM16.2264 4.15062C12.6415 4.15062 9.81133 6.98081 9.81133 10.5657C9.81133 14.1506 12.6415 16.9808 16.2264 16.9808C19.8113 16.9808 22.6415 14.1506 22.6415 10.5657C22.6415 6.98081 19.8113 4.15062 16.2264 4.15062Z"
            fill="#0171FE"
          />
          <path
            d="M27.5472 38.1131H4.90567C2.26416 38.1131 7.62939e-06 35.8489 7.62939e-06 33.2074C7.62939e-06 27.3583 4.71699 22.6414 10.566 22.6414H21.8868C27.7358 22.6414 32.4528 27.3583 32.4528 33.2074C32.4528 35.8489 30.1887 38.1131 27.5472 38.1131ZM10.566 24.9055C6.03774 24.9055 2.26416 28.6791 2.26416 33.2074C2.26416 34.7168 3.39623 35.8489 4.90567 35.8489H27.5472C29.0566 35.8489 30.1887 34.7168 30.1887 33.2074C30.1887 28.6791 26.4151 24.9055 21.8868 24.9055H10.566Z"
            fill="#0171FE"
          />
          <path
            d="M38.8679 19.2451H27.5472C26.9811 19.2451 26.4151 18.6791 26.4151 18.113C26.4151 17.547 26.9811 16.981 27.5472 16.981H38.8679C39.434 16.981 40 17.547 40 18.113C40 18.6791 39.434 19.2451 38.8679 19.2451Z"
            fill="#0171FE"
          />
          <path
            d="M33.2075 24.9057C32.6415 24.9057 32.0755 24.3397 32.0755 23.7736V12.4529C32.0755 11.8868 32.6415 11.3208 33.2075 11.3208C33.7736 11.3208 34.3396 11.8868 34.3396 12.4529V23.7736C34.3396 24.3397 33.7736 24.9057 33.2075 24.9057Z"
            fill="#0171FE"
          />
        </svg>
      );
    case flyingPaperPlan:
      return (
        <svg
          width="40"
          height="40"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clip-path="url(#clip0_1068_759)">
            <path
              d="M39.9925 0.754953C39.9864 0.708856 39.9794 0.664499 39.9655 0.619272C39.9507 0.569696 39.9307 0.524469 39.9081 0.478372C39.8959 0.453149 39.8916 0.426186 39.8768 0.401833C39.8672 0.386178 39.8516 0.375741 39.8403 0.360955C39.8116 0.320946 39.7776 0.286156 39.742 0.250496C39.7063 0.214836 39.6707 0.181785 39.6298 0.153084C39.615 0.142646 39.6054 0.127861 39.5907 0.119163C39.5663 0.104377 39.5393 0.100898 39.5141 0.0887217C39.4663 0.0652383 39.4193 0.045234 39.3689 0.0304482C39.3262 0.0191414 39.2845 0.0130531 39.241 0.00783455C39.1897 0.00174627 39.1401 -0.00173274 39.0888 0.000876518C39.041 0.00348578 38.9949 0.0130531 38.9479 0.0243599C38.9183 0.0313179 38.8879 0.0287087 38.8583 0.0391457L0.589135 13.0855C0.242973 13.202 0.00726914 13.5247 0.000311108 13.8909C-0.00664693 14.257 0.21601 14.5884 0.558693 14.7197L18.4382 21.5899L26.1643 39.4755C26.3025 39.7947 26.6174 40 26.9627 40C26.9775 40 26.9914 40 27.0062 39.9991C27.3689 39.9809 27.6811 39.7399 27.7916 39.3947L39.9681 1.12547C39.9768 1.09851 39.9742 1.07067 39.9794 1.04284C39.9899 0.994136 39.9977 0.947169 39.9994 0.897593C40.0012 0.848887 39.9986 0.80279 39.9925 0.754953ZM3.42192 13.9561L35.8785 2.89107L18.8757 19.8939L3.42192 13.9561ZM26.8383 36.6506L20.1238 21.1063L37.2258 4.00349L26.8383 36.6506Z"
              fill="#0171FE"
            />
            <path
              d="M13.6615 26.3388C13.3215 25.9987 12.7718 25.9987 12.4317 26.3388L0.255147 38.5154C-0.0849269 38.8554 -0.0849269 39.4051 0.255147 39.7452C0.424749 39.9148 0.647406 40 0.870063 40C1.09272 40 1.31538 39.9148 1.48498 39.7452L13.6615 27.5686C14.0016 27.2286 14.0016 26.6789 13.6615 26.3388Z"
              fill="#0171FE"
            />
            <path
              d="M15.9107 32.4272L9.82244 38.5155C9.48236 38.8555 9.48236 39.4052 9.82244 39.7453C9.99204 39.9149 10.2147 40.0001 10.4374 40.0001C10.66 40.0001 10.8827 39.9149 11.0523 39.7453L17.1406 33.657C17.4806 33.3169 17.4806 32.7672 17.1406 32.4272C16.8005 32.0871 16.2508 32.0871 15.9107 32.4272Z"
              fill="#0171FE"
            />
            <path
              d="M0.870063 30.4327C1.09272 30.4327 1.31538 30.3475 1.48498 30.1779L7.57326 24.0896C7.91333 23.7495 7.91333 23.1999 7.57326 22.8598C7.23319 22.5197 6.6835 22.5197 6.34343 22.8598L0.255147 28.9481C-0.0849269 29.2881 -0.0849269 29.8378 0.255147 30.1779C0.424749 30.3475 0.647406 30.4327 0.870063 30.4327Z"
              fill="#0171FE"
            />
          </g>
          <defs>
            <clipPath id="clip0_1068_759">
              <rect width="40" height="40" fill="white" />
            </clipPath>
          </defs>
        </svg>
      );
    case mail:
      return (
        <svg
          width="40"
          height="40"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill="none"
            d="M6.66665 6.66675H33.3333C35.1666 6.66675 36.6666 8.16675 36.6666 10.0001V30.0001C36.6666 31.8334 35.1666 33.3334 33.3333 33.3334H6.66665C4.83331 33.3334 3.33331 31.8334 3.33331 30.0001V10.0001C3.33331 8.16675 4.83331 6.66675 6.66665 6.66675Z"
            stroke="#0171FE"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            fill="none"
            d="M36.6666 10L20 21.6667L3.33331 10"
            stroke="#0171FE"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      );
    case circularCheck:
      return (
        <svg
          width="40"
          height="40"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M20 2.29175C10.2355 2.29175 2.29163 10.2357 2.29163 20.0001C2.29163 29.7645 10.2355 37.7084 20 37.7084C29.7644 37.7084 37.7083 29.7645 37.7083 20.0001C37.7083 10.2357 29.7644 2.29175 20 2.29175ZM20 35.6251C11.3843 35.6251 4.37496 28.6158 4.37496 20.0001C4.37496 11.3844 11.3843 4.37508 20 4.37508C28.6157 4.37508 35.625 11.3844 35.625 20.0001C35.625 28.6158 28.6157 35.6251 20 35.6251Z"
            fill="#0171FE"
          />
          <path
            d="M28.0807 14.2998L18.89 23.4903L14.1744 18.7751C13.7679 18.3682 13.1079 18.3682 12.7014 18.7751C12.2945 19.1818 12.2945 19.8414 12.7014 20.2481L18.1535 25.7C18.3568 25.9034 18.6235 26.0051 18.89 26.0051C19.1566 26.0051 19.4233 25.9034 19.6265 25.7L29.5536 15.7728C29.9606 15.3661 29.9606 14.7065 29.5536 14.2998C29.1471 13.8929 28.4871 13.8929 28.0807 14.2998Z"
            fill="#0171FE"
          />
        </svg>
      );

    case automobile:
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <g clip-path="url(#clip0_1210_619)">
            <path
              d="M23.8622 6.25595C23.7448 6.11277 23.5697 6.02919 23.386 6.02919H21.0502C20.4738 4.50883 19.7228 3.0192 18.8693 2.45691C15.9165 0.514365 8.08446 0.514365 5.1317 2.45691C4.27752 3.01982 3.52965 4.51129 2.95016 6.02919H0.614379C0.428795 6.02919 0.254272 6.11277 0.138128 6.25595C0.0213695 6.39853 -0.0253339 6.58719 0.0127662 6.76847L0.35874 8.44492C0.417734 8.73068 0.669686 8.93471 0.960353 8.93471H1.6523C0.984319 9.70533 0.660468 10.6757 0.656167 11.6466C0.651865 12.8462 1.10722 13.9303 1.93989 14.6972C1.94911 14.7052 1.95833 14.7107 1.96693 14.7187V17.0644C1.96693 17.5726 2.37989 17.9862 2.88871 17.9862H5.04321C5.55203 17.9862 5.96499 17.5726 5.96499 17.0644V16.1217H18.0347V17.0644C18.0347 17.5726 18.4477 17.9862 18.9565 17.9862H21.111C21.6186 17.9862 22.0328 17.5726 22.0328 17.0644V14.7648C22.9165 13.9554 23.3386 12.8419 23.3442 11.7302C23.3479 10.7261 23.0093 9.72008 22.3019 8.93348H23.04C23.3319 8.93348 23.5838 8.72945 23.6416 8.44308L23.9882 6.76725C24.0238 6.58719 23.979 6.39975 23.8622 6.25595ZM6.14381 3.99631C8.49742 2.44707 15.5023 2.44707 17.8547 3.99631C18.3119 4.29621 18.9 5.50868 19.4389 6.99339H4.56081C5.09852 5.5093 5.68661 4.29743 6.14381 3.99631ZM3.38708 11.8777C3.38708 10.876 4.19948 10.0648 5.20053 10.0648C6.20281 10.0648 7.01397 10.876 7.01397 11.8777C7.01397 12.8794 6.20281 13.6912 5.20053 13.6912C4.19948 13.6912 3.38708 12.8794 3.38708 11.8777ZM18.8232 13.6912C17.8221 13.6912 17.0097 12.8794 17.0097 11.8777C17.0097 10.876 17.8221 10.0648 18.8232 10.0648C19.8254 10.0648 20.6366 10.876 20.6366 11.8777C20.6366 12.8794 19.8242 13.6912 18.8232 13.6912Z"
              fill="#909ABC"
            />
          </g>
          <defs>
            <clipPath id="clip0_1210_619">
              <rect width="24" height="24" fill="white" />
            </clipPath>
          </defs>
        </svg>
      );
    case construction:
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M22 20.5224H2C1.586 20.5224 1.25 20.8594 1.25 21.2724C1.25 21.6864 1.586 22.0224 2 22.0224H22C22.414 22.0224 22.75 21.6864 22.75 21.2724C22.75 20.8594 22.414 20.5224 22 20.5224Z"
            fill="#909ABC"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M14.75 21.2724V2.74941C14.75 2.16941 14.462 1.62641 13.981 1.30041C13.5 0.975408 12.889 0.909408 12.35 1.12541L4.35 4.32541C3.686 4.59041 3.25 5.23441 3.25 5.95041V21.2724C3.25 21.6874 3.586 22.0224 4 22.0224H14C14.414 22.0224 14.75 21.6874 14.75 21.2724ZM7 19.0224H11C11.414 19.0224 11.75 18.6864 11.75 18.2724C11.75 17.8594 11.414 17.5224 11 17.5224H7C6.586 17.5224 6.25 17.8594 6.25 18.2724C6.25 18.6864 6.586 19.0224 7 19.0224ZM7 15.5224H11C11.414 15.5224 11.75 15.1864 11.75 14.7724C11.75 14.3594 11.414 14.0224 11 14.0224H7C6.586 14.0224 6.25 14.3594 6.25 14.7724C6.25 15.1864 6.586 15.5224 7 15.5224ZM7 12.0224H11C11.414 12.0224 11.75 11.6864 11.75 11.2724C11.75 10.8594 11.414 10.5224 11 10.5224H7C6.586 10.5224 6.25 10.8594 6.25 11.2724C6.25 11.6864 6.586 12.0224 7 12.0224ZM7 8.52241H11C11.414 8.52241 11.75 8.18641 11.75 7.77241C11.75 7.35941 11.414 7.02241 11 7.02241H7C6.586 7.02241 6.25 7.35941 6.25 7.77241C6.25 8.18641 6.586 8.52241 7 8.52241Z"
            fill="#909ABC"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M15.75 6.52241V21.2724C15.75 21.5414 15.69 21.7954 15.582 22.0224H20C20.414 22.0224 20.75 21.6874 20.75 21.2724V8.27241C20.75 7.30641 19.966 6.52241 19 6.52241H15.75Z"
            fill="#909ABC"
          />
        </svg>
      );
    case photoVideo:
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M10.4267 7C9.97692 5.56808 8.63917 4.52941 7.05882 4.52941C5.10958 4.52941 3.52941 6.10958 3.52941 8.05882C3.52941 9.76632 4.74195 11.1906 6.35294 11.5176V13.7059H1.41176C0.632069 13.7059 0 13.0738 0 12.2941V4.52941C0 3.74972 0.632069 3.11765 1.41176 3.11765H3.09315L3.76177 1.7804C4.00091 1.30211 4.48976 1 5.02449 1H9.09315C9.62789 1 10.1167 1.30211 10.3559 1.7804L11.0245 3.11765H12.7059C13.4856 3.11765 14.1176 3.74972 14.1176 4.52941V7H10.4267ZM8.89317 7H8.82353C7.45906 7 6.35294 8.10612 6.35294 9.47059V10.056C5.53045 9.76526 4.94118 8.98086 4.94118 8.05882C4.94118 6.88928 5.88928 5.94118 7.05882 5.94118C7.84265 5.94118 8.52702 6.36703 8.89317 7ZM19.0588 11.5313L21.248 9.77995C21.4984 9.57969 21.8094 9.47059 22.13 9.47059H22.9412C23.5259 9.47059 24 9.94464 24 10.5294V16.8824C24 17.4671 23.5259 17.9412 22.9412 17.9412H22.13C21.8094 17.9412 21.4984 17.8321 21.248 17.6318L19.0588 15.8804V17.9412C19.0588 18.5259 18.5848 19 18 19H8.82353C8.23876 19 7.76471 18.5259 7.76471 17.9412V9.47059C7.76471 8.88582 8.23876 8.41176 8.82353 8.41176H18C18.5848 8.41176 19.0588 8.88582 19.0588 9.47059V11.5313ZM16.4118 11.9412C16.8991 11.9412 17.2941 11.5461 17.2941 11.0588C17.2941 10.5715 16.8991 10.1765 16.4118 10.1765C15.9245 10.1765 15.5294 10.5715 15.5294 11.0588C15.5294 11.5461 15.9245 11.9412 16.4118 11.9412Z"
            fill="#909ABC"
          />
        </svg>
      );
    case business:
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <g clip-path="url(#clip0_1337_466)">
            <path
              d="M22 4.00005H17V3.00002C17 1.897 16.1031 1 15 1H9C7.89698 1 6.99998 1.89695 6.99998 3.00002V4H2.00002C0.896953 4.00005 0 4.897 0 6.00002V9.00002C0 10.1031 0.896953 11 2.00002 11H10V10.5C10 10.2236 10.2237 10 10.5 10H13.5C13.7764 10 14.0001 10.2236 14.0001 10.5V11H22.0001C23.103 11 24 10.1031 24 9.00002V6.00002C24 4.897 23.103 4.00005 22 4.00005ZM15 4.00005H9V3.00002H15V4.00005Z"
              fill="#909ABC"
            />
            <path
              d="M23.7226 11.5396C23.5523 11.4551 23.3486 11.4746 23.1982 11.5889C22.8427 11.8579 22.4287 12 21.9999 12H14V13.5C14 13.7764 13.7764 14 13.5 14H10.5C10.2236 14 9.99998 13.7764 9.99998 13.5V12H2.00002C1.5713 12 1.15725 11.8579 0.80175 11.5889C0.650859 11.4737 0.44775 11.4541 0.277312 11.5396C0.107437 11.6241 0 11.7974 0 11.9874V19C0 20.1031 0.896953 21.0001 2.00002 21.0001H22C23.103 21.0001 24 20.1031 24 19V11.9874C24 11.7974 23.8926 11.6241 23.7226 11.5396Z"
              fill="#909ABC"
            />
          </g>
          <defs>
            <clipPath id="clip0_1337_466">
              <rect width="24" height="24" fill="white" />
            </clipPath>
          </defs>
        </svg>
      );
    case realEstate:
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <g clip-path="url(#clip0_1338_487)">
            <path
              d="M16.2258 12.4286C16.5741 12.6753 17.0221 12.7331 17.4215 12.5829L18.5787 12.1543L18.1501 11.3571C17.8632 10.8564 17.9811 10.2215 18.4287 9.85714V6.38571L12.0001 1.98428L5.57153 6.38571V9.99857C6.00495 10.3891 6.12107 11.0225 5.85439 11.5414L5.64868 11.97C6.16566 12.2113 6.71871 12.3662 7.28582 12.4286C7.53877 12.4075 7.7896 12.366 8.03582 12.3043C8.49431 12.1974 8.95974 12.123 9.42868 12.0814C9.55187 11.833 9.69521 11.595 9.85725 11.37V8.57143C9.85725 8.09804 10.241 7.71428 10.7144 7.71428H13.2858C13.7592 7.71428 14.143 8.09804 14.143 8.57143V10.89L16.2258 12.4286ZM12.0001 6.85714C11.0533 6.85714 10.2858 6.08963 10.2858 5.14285C10.2858 4.19608 11.0533 3.42857 12.0001 3.42857C12.9469 3.42857 13.7144 4.19608 13.7144 5.14285C13.7144 5.59751 13.5338 6.03355 13.2123 6.35504C12.8908 6.67653 12.4548 6.85714 12.0001 6.85714Z"
              fill="#909ABC"
            />
            <path
              d="M10.7144 10.5C11.4675 9.95852 12.4603 9.88406 13.2858 10.3071V8.57143H10.7144V10.5Z"
              fill="#909ABC"
            />
            <path
              d="M12 6.00001C12.4734 6.00001 12.8571 5.61625 12.8571 5.14286C12.8571 4.66948 12.4734 4.28572 12 4.28572C11.5266 4.28572 11.1428 4.66948 11.1428 5.14286C11.1428 5.61625 11.5266 6.00001 12 6.00001Z"
              fill="#909ABC"
            />
            <path
              d="M4.09712 6.3555L11.7557 1.11407C11.9026 1.01215 12.0974 1.01215 12.2443 1.11407L19.9028 6.35121C20.0981 6.48492 20.3648 6.43504 20.4986 6.23978C20.6294 6.04533 20.5797 5.78187 20.3871 5.64835L12.2443 0.0769245C12.1726 0.0269056 12.0874 -1.14572e-05 12 -0.000218354C11.9138 -0.000841304 11.8297 0.0261973 11.76 0.0769245L3.61712 5.64835C3.52567 5.71208 3.46264 5.80893 3.44141 5.91835C3.41821 6.03115 3.44137 6.14854 3.50569 6.24407C3.63921 6.43667 3.90267 6.4863 4.09712 6.3555Z"
              fill="#909ABC"
            />
            <path
              d="M4.5128 18.9129C4.25951 18.7977 3.96076 18.9088 3.84423 19.1614L3.36852 20.19C3.25017 20.4433 3.35954 20.7445 3.6128 20.8629C3.86607 20.9812 4.16731 20.8718 4.28566 20.6186L4.76137 19.59C4.81258 19.4651 4.81258 19.325 4.76137 19.2C4.72107 19.0744 4.6313 18.9707 4.5128 18.9129Z"
              fill="#909ABC"
            />
            <path
              d="M5.11714 10.7957C5.07457 10.6845 4.98761 10.596 4.87714 10.5514L0 8.57143V20.1429H0.321429C0.477856 20.1409 0.620772 20.0538 0.694286 19.9157L5.09143 11.1429C5.14872 11.036 5.15807 10.9099 5.11714 10.7957Z"
              fill="#909ABC"
            />
            <path
              d="M7.02851 19.3372C6.96928 19.1765 6.84919 19.0457 6.69422 18.9729C6.60911 18.9356 6.51713 18.9166 6.42422 18.9172C6.3483 18.9174 6.27296 18.9305 6.20137 18.9557C6.04184 19.0151 5.91238 19.1353 5.84137 19.29L5.14279 20.8457C5.0787 21.0038 5.0787 21.1805 5.14279 21.3386C5.20049 21.4991 5.32123 21.6292 5.47708 21.6986C5.63321 21.7648 5.80952 21.7648 5.96565 21.6986C6.12629 21.6394 6.25716 21.5193 6.32994 21.3643L7.04565 19.8086C7.10076 19.6551 7.09462 19.4862 7.02851 19.3372Z"
              fill="#909ABC"
            />
            <path
              d="M17.7214 13.3671C17.0517 13.6181 16.3009 13.5184 15.7199 13.1014L13.1099 11.1986C12.4839 10.7411 11.6214 10.7914 11.0528 11.3186C10.6339 11.7061 10.2972 12.174 10.0628 12.6943L9.06851 14.8971C8.93159 15.2143 9.05533 15.5836 9.35565 15.7543H9.38565C9.66023 15.8879 9.99023 15.8212 10.1914 15.5914L11.8799 13.71L11.7257 13.5729C11.5602 13.4139 11.5492 13.1529 11.7008 12.9806C11.8524 12.8084 12.1127 12.7861 12.2914 12.93L18.4885 18.4286L21.3428 17.2457L18.9899 12.8914L17.7214 13.3671Z"
              fill="#909ABC"
            />
            <path
              d="M19.1101 10.4186C19.0042 10.4557 18.9197 10.537 18.8787 10.6414C18.8448 10.733 18.8526 10.8347 18.9001 10.92L23.3015 19.0629C23.3813 19.194 23.5252 19.2725 23.6787 19.2686H24.0001V8.54143L19.1101 10.4186Z"
              fill="#909ABC"
            />
            <path
              d="M12.5143 14.31L10.8215 16.1957C10.5319 16.5269 10.1128 16.7161 9.67288 16.7143C9.45038 16.7146 9.23066 16.6648 9.03002 16.5686H8.98717C8.25232 16.2017 7.93976 15.319 8.28002 14.5714L9.00002 12.9943C8.73002 13.0372 8.47288 13.0886 8.23288 13.14C7.9226 13.219 7.60541 13.2678 7.28574 13.2857C6.58407 13.2169 5.89965 13.027 5.26288 12.7243L2.20288 18.8186L2.90574 19.1529L3.06431 18.8014C3.38048 18.1188 4.19001 17.8214 4.87288 18.1372C5.06084 18.2225 5.22548 18.3519 5.35288 18.5143C5.79526 18.0484 6.48737 17.9203 7.06717 18.1972C7.53843 18.4142 7.86304 18.8605 7.92431 19.3757C8.64605 19.2317 9.36492 19.6358 9.61717 20.3272C9.64512 20.4096 9.66661 20.4942 9.68145 20.58C9.92873 20.5413 10.1819 20.5755 10.41 20.6786C10.722 20.8212 10.964 21.0825 11.0823 21.4044C11.2006 21.7264 11.1854 22.0822 11.04 22.3929L10.7572 22.9714L11.9057 23.9014C12.0701 24.0135 12.2823 24.0279 12.4602 23.9389C12.6381 23.8499 12.754 23.6716 12.7629 23.4729C12.7642 23.3273 12.7052 23.1877 12.6 23.0872L12.12 22.6072C12.0389 22.5267 11.9933 22.4171 11.9933 22.3029C11.9933 22.1886 12.0389 22.0791 12.12 21.9986C12.2005 21.9174 12.31 21.8718 12.4243 21.8718C12.5386 21.8718 12.6481 21.9174 12.7286 21.9986L13.2086 22.4786L13.5772 22.8514C13.7686 23.0316 14.0672 23.0316 14.2586 22.8514C14.3492 22.7621 14.4001 22.6401 14.4001 22.5129C14.4001 22.3856 14.3492 22.2637 14.2586 22.1743L12.5443 20.46C12.3763 20.292 12.3763 20.0195 12.5443 19.8514C12.7124 19.6834 12.9848 19.6834 13.1529 19.8514L14.8672 21.5657L14.9143 21.6129C15.0494 21.7484 15.2329 21.8246 15.4243 21.8246C15.6157 21.8246 15.7992 21.7484 15.9343 21.6129C16.0776 21.4726 16.1555 21.2786 16.1489 21.0782C16.1424 20.8778 16.0521 20.6893 15.9 20.5586L13.8557 18.7672C13.7838 18.7005 13.737 18.6113 13.7229 18.5143C13.7211 18.5015 13.7211 18.4885 13.7229 18.4757C13.7159 18.3673 13.753 18.2607 13.8257 18.18C13.864 18.1386 13.9089 18.1038 13.9586 18.0772C14.0392 18.0409 14.129 18.0304 14.2157 18.0472C14.2371 18.0451 14.2586 18.0451 14.28 18.0472C14.3356 18.0645 14.3868 18.0938 14.43 18.1329L14.9229 18.5614L17.0057 20.3872C17.3332 20.65 17.8097 20.6083 18.0865 20.2924C18.3633 19.9766 18.3422 19.4988 18.0386 19.2086L12.5143 14.31Z"
              fill="#909ABC"
            />
            <path
              d="M10.0328 21.4586C9.97694 21.4364 9.91708 21.4262 9.85705 21.4286C9.68966 21.4282 9.53735 21.5253 9.46705 21.6772L9.06848 22.5343L8.93562 22.8214C8.89158 22.927 8.89158 23.0459 8.93562 23.1514C8.97521 23.2578 9.05535 23.3441 9.15848 23.3914C9.26233 23.4408 9.38167 23.4464 9.48967 23.4069C9.59766 23.3674 9.68526 23.2862 9.73277 23.1814L10.2685 22.02C10.3122 21.9144 10.3119 21.7956 10.2677 21.6902C10.2234 21.5847 10.1388 21.5013 10.0328 21.4586Z"
              fill="#909ABC"
            />
            <path
              d="M8.46005 20.2629C8.37606 20.2218 8.28353 20.2012 8.19005 20.2029C8.11413 20.2031 8.03879 20.2162 7.96719 20.2414C7.80841 20.302 7.67934 20.4219 7.60719 20.5757L7.06719 21.7414C6.91689 22.0645 7.05696 22.4483 7.38005 22.5986C7.70314 22.7489 8.08689 22.6088 8.23719 22.2857L8.27148 22.2129L8.76862 21.1286C8.84198 20.9739 8.84971 20.7962 8.79005 20.6357C8.73591 20.4714 8.61658 20.3366 8.46005 20.2629Z"
              fill="#909ABC"
            />
          </g>
          <defs>
            <clipPath id="clip0_1338_487">
              <rect width="24" height="24" fill="white" />
            </clipPath>
          </defs>
        </svg>
      );
    case crafts:
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M7.94783 20.4432H17.9948C17.9101 19.9104 17.6476 19.5291 17.3926 19.1587C17.1075 18.7438 16.8123 18.3152 16.8038 17.7196C16.7852 16.3949 18.5073 14.7515 21.3376 14.3285V10.4711H2.66235C2.84659 13.6598 5.31775 15.6662 6.87705 16.1923C9.06416 16.9299 9.37641 17.9876 9.36769 18.5523C9.35694 19.2662 8.83497 19.9413 7.94783 20.4432Z"
            fill="#909ABC"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M10.718 3.84708C10.7758 4.04723 10.8307 4.24967 10.8813 4.45295C10.9325 4.65751 10.9796 4.8624 11.0226 5.0656C11.0823 5.0601 11.1432 5.0507 11.2042 5.03741L20.2852 3.07357C20.5533 3.01548 20.7446 2.87587 20.8148 2.76292C20.8399 2.72237 20.8453 2.69333 20.8421 2.67944L20.5152 1.36262C20.5117 1.34891 20.4933 1.32571 20.4519 1.30166C20.3372 1.23486 20.1031 1.20091 19.8386 1.275L10.8939 3.78612C10.8329 3.80322 10.7738 3.8238 10.718 3.84708Z"
            fill="#909ABC"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M3.96118 20.951H21.0823V22.7667H3.96118V20.951Z"
            fill="#909ABC"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M6.169 2.175C6.04158 2.84471 6.17341 4.13232 6.51927 5.53668C6.86361 6.933 7.34925 8.13307 7.78156 8.67104L10.739 7.93681C10.8697 7.25931 10.7378 5.97162 10.3886 4.57597C10.0376 3.17279 9.55194 1.97298 9.12607 1.44102L6.169 2.175Z"
            fill="#909ABC"
          />
        </svg>
      );
    case jewelryWatches:
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M12 12.5C15.1365 12.5 17.914 10.9655 19.6455 8.6155L18.2835 8.208C17.217 7.8885 16.5005 6.9265 16.5005 5.8125V3C16.5005 2.173 15.8275 1.5 15.0005 1.5H9.00047C8.17347 1.5 7.50047 2.173 7.50047 3V5.8125C7.50047 6.926 6.78397 7.8885 5.71747 8.208L4.35547 8.6155C6.08647 10.966 8.86447 12.5 12.001 12.5H12Z"
            fill="#909ABC"
          />
          <path
            d="M21.9256 9.2975L20.6641 8.92C18.8596 11.5525 15.8866 13.314 12.5001 13.4745V14.5H12.7691C13.0326 14.5 13.2766 14.638 13.4121 14.864L13.8101 15.527C13.9766 15.8045 13.9466 16.1575 13.7351 16.4025L12.5681 17.757C12.2691 18.1045 11.7311 18.1045 11.4316 17.757L10.2646 16.4025C10.0536 16.1575 10.0231 15.8045 10.1896 15.527L10.5876 14.864C10.7231 14.638 10.9671 14.5 11.2306 14.5H11.4996V13.4745C8.11255 13.314 5.14005 11.5525 3.33555 8.92L2.07405 9.2975C1.39905 9.4995 0.949051 10.158 1.00405 10.863C1.43255 16.367 6.05355 22.5 11.9996 22.5C17.9456 22.5 22.5666 16.367 22.9951 10.863C23.0501 10.158 22.6001 9.4995 21.9251 9.2975H21.9256Z"
            fill="#909ABC"
          />
        </svg>
      );
    case others:
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M21 1.25H15C14.0335 1.25 13.25 2.0335 13.25 3V9C13.25 9.9665 14.0335 10.75 15 10.75H21C21.9665 10.75 22.75 9.9665 22.75 9V3C22.75 2.0335 21.9665 1.25 21 1.25Z"
            fill="#909ABC"
          />
          <path
            d="M22.7333 16.7627L19.2372 13.2666C18.9086 12.9392 18.4637 12.7554 17.9999 12.7554C17.5361 12.7554 17.0911 12.9392 16.7626 13.2666L13.2665 16.7627C12.9389 17.0911 12.7549 17.5361 12.7549 18C12.7549 18.4639 12.9389 18.9089 13.2665 19.2373L16.7626 22.7334C17.091 23.0611 17.536 23.2451 17.9999 23.2451C18.4638 23.2451 18.9088 23.0611 19.2372 22.7334L22.7333 19.2373C23.0611 18.909 23.2452 18.464 23.2452 18C23.2452 17.536 23.0611 17.091 22.7333 16.7627Z"
            fill="#909ABC"
          />
          <path
            d="M9 1.25H3C2.0335 1.25 1.25 2.0335 1.25 3V9C1.25 9.9665 2.0335 10.75 3 10.75H9C9.9665 10.75 10.75 9.9665 10.75 9V3C10.75 2.0335 9.9665 1.25 9 1.25Z"
            fill="#909ABC"
          />
          <path
            d="M9 13.25H3C2.0335 13.25 1.25 14.0335 1.25 15V21C1.25 21.9665 2.0335 22.75 3 22.75H9C9.9665 22.75 10.75 21.9665 10.75 21V15C10.75 14.0335 9.9665 13.25 9 13.25Z"
            fill="#909ABC"
          />
        </svg>
      );
    case 'card':
    case 'default':
      return (
        <svg
          className={classes}
          width="24"
          height="16"
          viewBox="0 0 24 16"
          xmlns="http://www.w3.org/2000/svg"
          {...roleInfo}
        >
          <g fill="none" fillRule="evenodd">
            <rect fill="#4A4A4A" height="16" rx="2" width="24" />
            <rect fill="#F5F5F5" height="4" width="24" y="3" />
            <rect fill="#D8D8D8" height="2" width="6" x="3" y="10" />
            <rect fill="#D8D8D8" height="2" width="3" x="11" y="10" />
            <rect fill="#D8D8D8" height="2" width="3" x="16" y="10" />
          </g>
        </svg>
      );
    default:
      // Generic category icon shown for any category not mapped to a specific icon
      return (
        <svg
          width="40"
          height="40"
          viewBox="0 0 40 40"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          {...roleInfo}
        >
          <path
            d="M21.667 3.333H10a3.333 3.333 0 0 0-3.333 3.334v26.666A3.333 3.333 0 0 0 10 36.667h20a3.333 3.333 0 0 0 3.333-3.334V15L21.667 3.333Z"
            stroke="#909ABC"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M21.667 3.333V15H33.333"
            stroke="#909ABC"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M26.667 21.667H13.333"
            stroke="#909ABC"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M26.667 28.333H13.333"
            stroke="#909ABC"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M16.667 15H15H13.333"
            stroke="#909ABC"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
  }
};

export default IconCard;
